import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AxiosInstance } from 'axios';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { ConfirmModal } from '@/components/ConfirmModal';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PhoneInput, parsePhoneString, formatPhoneString } from '@/components/ui/PhoneInput';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { DateInput } from '@/components/ui/DateInput';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { ALL_LEAD_COLUMNS, FACTORY_DEFAULT_COLUMNS } from '@/lib/leadColumns';
import { renderBlocksToHtml, type EmailBlock } from '@/lib/emailBlocks';
import { getCountryNames, getStateNames, getCityNames } from '@/lib/geoApi';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  Building2,
  Calendar,
  Check,
  Columns3,
  Copy,
  Eye,
  EyeOff,
  Flag,
  ImagePlus,
  Inbox,
  Loader2,
  Mail,
  MessageSquare,
  Palette,
  Pencil,
  Phone,
  Plug,
  Send,
  Plus,
  SlidersHorizontal,
  ToggleLeft,
  ToggleRight,
  Trash2,
  Upload,
  User as UserIcon,
  Users,
  Video,
  X,
  Zap,
  AlertTriangle,
  Sparkles,
} from 'lucide-react';
import { toast } from '@/components/ui/toast';
import { CommunicationsSettings } from '@/components/settings/CommunicationsSettings';
import { formatDate } from '@/lib/dateFormat';
import { SelectField } from '@/components/ui/SelectField';

// 'profile' removed — the dedicated /profile page (linked from the avatar
// dropdown in the header) is the canonical place to edit personal info.
// Having a duplicate tab inside /settings was confusing the support team.
type Tab = 'workspace' | 'brand' | 'lead-columns' | 'lead-statuses' | 'lead-sources' | 'financial-year' | 'user-fields' | 'custom-fields' | 'integrations' | 'communications';
type LogoVariantKey = 'square' | 'rectangular';

interface NamedItem {
  id: string;
  name: string;
  isActive: boolean;
}

interface LogoVariantState {
  zoom: number;
  offsetX: number;
  offsetY: number;
  direct?: boolean; // true = this size was uploaded directly (use as-is, skip crop-from-source)
}

interface BookingPageSettings {
  welcomeMessage: string;
  thankYouMessage: string;
  showPhone: boolean;
  showEmail: boolean;
  showWebsite: boolean;
  footerNote: string;
}

interface BrandFormState {
  organizationName: string;
  address: string;
  country: string;
  state: string;
  city: string;
  zipcode: string;
  phones: string[];
  emails: string[];
  websiteUrl: string;
  dateFormat: string;
  currency: string;
  timezone: string;
  language: string;
  primaryColor: string;
  logoSource: string;
  logoSquare: string;
  logoRectangular: string;
  logoVariants: Record<LogoVariantKey, LogoVariantState>;
  bookingPageSettings: BookingPageSettings;
}

const DATE_FORMAT_OPTIONS = ['DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'];
const CURRENCY_OPTIONS = ['INR', 'USD', 'AED', 'EUR', 'GBP', 'SAR', 'SGD'];
const TIMEZONE_OPTIONS = [
  'Asia/Kolkata',
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Dubai',
  'Asia/Singapore',
  'Australia/Sydney',
];
const LANGUAGE_OPTIONS = ['English', 'Hindi', 'Arabic', 'French', 'German', 'Spanish'];

const LOGO_VARIANT_META: Record<LogoVariantKey, { label: string; width: number; height: number; previewClassName: string }> = {
  square: { label: 'Square Logo', width: 320, height: 320, previewClassName: 'aspect-square max-w-[220px]' },
  rectangular: { label: 'Rectangular Logo', width: 480, height: 180, previewClassName: 'aspect-[8/3] max-w-[320px]' },
};

const TEXTAREA_CLASSNAME = 'flex min-h-[104px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
const SELECT_CLASSNAME = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';

// Country / state / city all come from our own DB master via lib/geoApi.
const fetchStates = getStateNames;
const fetchCities = getCityNames;

function createDefaultBrandForm(): BrandFormState {
  return {
    organizationName: '',
    address: '',
    country: '',
    state: '',
    city: '',
    zipcode: '',
    phones: [''],
    emails: [''],
    websiteUrl: '',
    dateFormat: 'DD/MM/YYYY',
    currency: 'INR',
    timezone: 'Asia/Kolkata',
    language: 'English',
    primaryColor: '#6366F1',
    logoSource: '',
    logoSquare: '',
    logoRectangular: '',
    logoVariants: {
      square: { zoom: 1, offsetX: 0, offsetY: 0 },
      rectangular: { zoom: 1, offsetX: 0, offsetY: 0 },
    },
    bookingPageSettings: {
      welcomeMessage: '',
      thankYouMessage: '',
      showPhone: true,
      showEmail: true,
      showWebsite: true,
      footerNote: '',
    },
  };
}

function normalizeBrandSettings(raw: any): BrandFormState {
  const defaults = createDefaultBrandForm();
  const square = raw?.logoVariants?.square || {};
  const rectangular = raw?.logoVariants?.rectangular || {};

  return {
    ...defaults,
    organizationName: raw?.organizationName || '',
    address: raw?.address || '',
    country: raw?.country || '',
    state: raw?.state || '',
    city: raw?.city || '',
    zipcode: raw?.zipcode || '',
    phones: Array.isArray(raw?.phones) && raw.phones.length > 0 ? raw.phones : defaults.phones,
    emails: Array.isArray(raw?.emails) && raw.emails.length > 0 ? raw.emails : defaults.emails,
    websiteUrl: raw?.websiteUrl || '',
    dateFormat: raw?.dateFormat || defaults.dateFormat,
    currency: raw?.currency || defaults.currency,
    timezone: raw?.timezone || defaults.timezone,
    language: raw?.language || defaults.language,
    primaryColor: raw?.primaryColor || defaults.primaryColor,
    logoSource: raw?.logoSource || '',
    logoSquare: raw?.logoSquare || '',
    logoRectangular: raw?.logoRectangular || '',
    logoVariants: {
      square: {
        zoom: typeof square.zoom === 'number' ? square.zoom : defaults.logoVariants.square.zoom,
        offsetX: typeof square.offsetX === 'number' ? square.offsetX : defaults.logoVariants.square.offsetX,
        offsetY: typeof square.offsetY === 'number' ? square.offsetY : defaults.logoVariants.square.offsetY,
      },
      rectangular: {
        zoom: typeof rectangular.zoom === 'number' ? rectangular.zoom : defaults.logoVariants.rectangular.zoom,
        offsetX: typeof rectangular.offsetX === 'number' ? rectangular.offsetX : defaults.logoVariants.rectangular.offsetX,
        offsetY: typeof rectangular.offsetY === 'number' ? rectangular.offsetY : defaults.logoVariants.rectangular.offsetY,
      },
    },
    bookingPageSettings: {
      welcomeMessage: raw?.bookingPageSettings?.welcomeMessage ?? '',
      thankYouMessage: raw?.bookingPageSettings?.thankYouMessage ?? '',
      showPhone: raw?.bookingPageSettings?.showPhone !== false,
      showEmail: raw?.bookingPageSettings?.showEmail !== false,
      showWebsite: raw?.bookingPageSettings?.showWebsite !== false,
      footerNote: raw?.bookingPageSettings?.footerNote ?? '',
    },
  };
}

function clamp(value: number, min: number, max: number) {
  return Math.min(Math.max(value, min), max);
}

function readFileAsDataUrl(file: File) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function loadImage(src: string) {
  return new Promise<HTMLImageElement>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

async function generateCroppedLogo(
  src: string,
  variant: LogoVariantState,
  width: number,
  height: number,
) {
  const image = await loadImage(src);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');

  if (!context) throw new Error('Canvas is not supported');

  context.clearRect(0, 0, width, height);

  const scale = Math.max(width / image.width, height / image.height) * variant.zoom;
  const drawWidth = image.width * scale;
  const drawHeight = image.height * scale;
  const x = (width - drawWidth) / 2 + (variant.offsetX / 100) * ((drawWidth - width) / 2);
  const y = (height - drawHeight) / 2 + (variant.offsetY / 100) * ((drawHeight - height) / 2);

  context.drawImage(image, x, y, drawWidth, drawHeight);
  return canvas.toDataURL('image/png');
}

// ── Custom Fields Tab ──────────────────────────────────────────────────────────
const ENTITY_TYPES = ['LEAD', 'USER'];
const FIELD_TYPES = ['text', 'number', 'date', 'dropdown', 'checkbox', 'textarea'];

interface CustomField { id: string; entityType: string; label: string; fieldType: string; keyValues: any[]; sortOrder: number; isRequired: boolean; isActive: boolean; }
interface CFForm { entityType: string; label: string; fieldType: string; keyValues: string; sortOrder: number; isRequired: boolean; isActive: boolean; }

function CFDrawer({ field, onClose, onSaved }: { field?: CustomField; onClose: () => void; onSaved: () => void }) {
  const isEdit = !!field;
  const [form, setForm] = useState<CFForm>({
    entityType: field?.entityType ?? 'LEAD',
    label: field?.label ?? '',
    fieldType: field?.fieldType ?? 'text',
    keyValues: field ? (field.keyValues as string[]).join('\n') : '',
    sortOrder: field?.sortOrder ?? 0,
    isRequired: field?.isRequired ?? false,
    isActive: field?.isActive ?? true,
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!form.label.trim()) return;
    setSaving(true);
    const payload = {
      entityType: form.entityType, label: form.label.trim(),
      fieldType: form.fieldType,
      keyValues: form.fieldType === 'dropdown' ? form.keyValues.split('\n').map(s => s.trim()).filter(Boolean) : [],
      sortOrder: form.sortOrder, isRequired: form.isRequired, isActive: form.isActive,
    };
    try {
      if (isEdit) await api.patch(`/settings/custom-fields/${field!.id}`, payload);
      else await api.post('/settings/custom-fields', payload);
      onSaved();
    } catch (err: any) { toast('error', err?.response?.data?.message || 'Failed to save'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-[460px] bg-white shadow-2xl flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-slate-900">{isEdit ? 'Edit Custom Field' : 'New Custom Field'}</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          <div><Label>Entity Type</Label>
            <SelectField className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm mt-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={form.entityType} onChange={e => setForm(p => ({ ...p, entityType: e.target.value }))} disabled={isEdit}>
              {ENTITY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </SelectField>
          </div>
          <div><Label>Label <span className="text-red-500">*</span></Label>
            <Input className="mt-1.5" placeholder="e.g. Company Size" value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} />
          </div>
          <div><Label>Field Type</Label>
            <SelectField className="w-full h-10 px-3 rounded-lg border border-gray-200 text-sm mt-1.5 focus:outline-none focus:ring-2 focus:ring-indigo-500"
              value={form.fieldType} onChange={e => setForm(p => ({ ...p, fieldType: e.target.value }))}>
              {FIELD_TYPES.map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
            </SelectField>
          </div>
          {form.fieldType === 'dropdown' && (
            <div><Label>Options (one per line)</Label>
              <textarea className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm mt-1.5 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows={4} placeholder="Option 1&#10;Option 2&#10;Option 3"
                value={form.keyValues} onChange={e => setForm(p => ({ ...p, keyValues: e.target.value }))} />
            </div>
          )}
          <div><Label>Sort Order</Label>
            <Input type="number" className="mt-1.5 w-28" value={form.sortOrder} onChange={e => setForm(p => ({ ...p, sortOrder: Number(e.target.value) }))} />
          </div>
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="h-4 w-4 accent-indigo-600" checked={form.isRequired} onChange={e => setForm(p => ({ ...p, isRequired: e.target.checked }))} />
              <span className="text-sm text-slate-700">Required</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" className="h-4 w-4 accent-indigo-600" checked={form.isActive} onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))} />
              <span className="text-sm text-slate-700">Active</span>
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-100">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving || !form.label.trim()}>
            {saving && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
            {isEdit ? 'Save Changes' : 'Create Field'}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ── Lead Statuses Tab ─────────────────────────────────────────────────────────

const STATUS_PRESET_COLORS = [
  '#3B82F6', '#8B5CF6', '#F59E0B', '#22C55E', '#06B6D4', '#EF4444',
  '#EC4899', '#64748B', '#10B981', '#DC2626', '#6B7280', '#F97316',
];

interface LeadStatus {
  id: string;
  name: string;
  color: string;
  isDefault: boolean;
  isActive: boolean;
  order: number;
  skipInBroadcasts?: boolean;
  // 'system' = super-admin default (read-only here),
  // 'own' = tenant's own row (full CRUD).
  source?: 'system' | 'own';
}

function LeadStatusesTab() {
  const [statuses, setStatuses] = useState<LeadStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addColor, setAddColor] = useState(STATUS_PRESET_COLORS[0]);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchStatuses = () => {
    setLoading(true);
    api.get('/leads/statuses')
      .then(({ data }) => setStatuses(data))
      .catch(() => toast('error', 'Failed to load statuses'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchStatuses(); }, []);

  const handleAdd = async () => {
    if (!addName.trim()) return;
    setAdding(true);
    try {
      await api.post('/leads/statuses', { name: addName.trim(), color: addColor });
      setAddName('');
      setAddColor(STATUS_PRESET_COLORS[0]);
      setShowAdd(false);
      fetchStatuses();
      toast('success', 'Status created');
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to create status');
    } finally {
      setAdding(false);
    }
  };

  const handleSaveEdit = async (id: string) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await api.patch(`/leads/statuses/${id}`, { name: editName.trim(), color: editColor });
      setEditId(null);
      fetchStatuses();
      toast('success', 'Status updated');
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to update status');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (s: LeadStatus) => {
    try {
      await api.patch(`/leads/statuses/${s.id}`, { isActive: !s.isActive });
      fetchStatuses();
    } catch {
      toast('error', 'Failed to toggle status');
    }
  };

  const handleToggleSkip = async (s: LeadStatus) => {
    try {
      await api.patch(`/leads/statuses/${s.id}`, { skipInBroadcasts: !s.skipInBroadcasts });
      toast('success', !s.skipInBroadcasts ? 'Leads in this status will be skipped in broadcasts' : 'Status re-included in broadcasts');
      fetchStatuses();
    } catch {
      toast('error', 'Failed to update');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/leads/statuses/${id}`);
      fetchStatuses();
      toast('success', 'Status deleted');
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete status');
    }
  };

  const handleMove = async (index: number, direction: 'up' | 'down') => {
    const newStatuses = [...statuses];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= newStatuses.length) return;
    [newStatuses[index], newStatuses[swapIndex]] = [newStatuses[swapIndex], newStatuses[index]];
    setStatuses(newStatuses);
    try {
      await api.patch('/leads/statuses/reorder', { ids: newStatuses.map(s => s.id) });
    } catch {
      toast('error', 'Failed to reorder');
      fetchStatuses();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Lead Statuses</CardTitle>
            <CardDescription>Manage the statuses available for leads</CardDescription>
          </div>
          <Button
            size="sm"
            onClick={() => setShowAdd(true)}
            className="h-8 bg-[#6366F1] px-3 text-white hover:bg-[#5254cc]"
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Status
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Inline Add Form */}
        {showAdd && (
          <div className="flex items-center gap-3 rounded-lg border border-dashed border-slate-200 bg-slate-50/50 p-3">
            <Input
              placeholder="Status name..."
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              className="h-8 flex-1 text-sm"
              autoFocus
            />
            <div className="flex gap-1">
              {STATUS_PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setAddColor(c)}
                  className={`h-6 w-6 rounded-full border-2 transition-all ${addColor === c ? 'border-slate-800 scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <Button size="sm" onClick={handleAdd} disabled={adding || !addName.trim()} className="h-8 bg-[#6366F1] text-white hover:bg-[#5254cc]">
              {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            </Button>
            <button onClick={() => { setShowAdd(false); setAddName(''); }} className="text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        {/* Status List */}
        <div className="overflow-hidden rounded-md border border-slate-100">
          <div className="divide-y divide-slate-100">
            {statuses.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">No statuses configured</p>
            ) : (
              statuses.map((s, idx) => {
                const isSystem = s.source === 'system';
                // Tenant-only range for move up/down bounds — system rows
                // sit above and shouldn't constrain reorder limits.
                const ownRows = statuses.filter((x) => x.source !== 'system');
                const ownIdx = ownRows.findIndex((x) => x.id === s.id);
                return (
                <div key={s.id} className="group flex items-center gap-3 bg-white px-3 py-2.5 hover:bg-slate-50">
                  {editId === s.id && !isSystem ? (
                    <>
                      <div className="flex gap-1 flex-shrink-0">
                        {STATUS_PRESET_COLORS.map((c) => (
                          <button
                            key={c}
                            type="button"
                            onClick={() => setEditColor(c)}
                            className={`h-5 w-5 rounded-full border-2 transition-all ${editColor === c ? 'border-slate-800 scale-110' : 'border-transparent'}`}
                            style={{ backgroundColor: c }}
                          />
                        ))}
                      </div>
                      <Input
                        value={editName}
                        onChange={(e) => setEditName(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') handleSaveEdit(s.id);
                          if (e.key === 'Escape') setEditId(null);
                        }}
                        className="h-7 flex-1 text-sm"
                        autoFocus
                      />
                      <button onClick={() => handleSaveEdit(s.id)} className="text-emerald-600 hover:text-emerald-700" disabled={saving}>
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                      </button>
                      <button onClick={() => setEditId(null)} className="text-slate-400 hover:text-slate-600">
                        <X className="h-4 w-4" />
                      </button>
                    </>
                  ) : (
                    <>
                      <span className="h-3 w-3 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                      <span className={`flex-1 text-sm font-medium ${s.isActive ? 'text-slate-800' : 'text-slate-400 line-through'}`}>
                        {s.name}
                      </span>

                      {/* Default vs Custom badge — replaces the legacy
                          isDefault pill and makes the ownership obvious. */}
                      {isSystem ? (
                        <span className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-600" title="Platform default — read-only">
                          🔒 Default
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded bg-emerald-50 px-1.5 py-0.5 text-[10px] font-semibold text-emerald-700">
                          Custom
                        </span>
                      )}

                      {/* Broadcast inclusion toggle — leads in a "skip" status are
                          auto-excluded from WhatsApp broadcasts (e.g. Converted). */}
                      {!isSystem && (
                        <button
                          onClick={() => handleToggleSkip(s)}
                          title="Toggle whether leads in this status receive WhatsApp broadcasts"
                          className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-semibold transition-colors ${s.skipInBroadcasts ? 'bg-amber-50 text-amber-700 hover:bg-amber-100' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}
                        >
                          {s.skipInBroadcasts ? 'Broadcast: skip' : 'Broadcast: send'}
                        </button>
                      )}

                      {/* Reorder arrows — only on tenant-owned rows */}
                      {!isSystem && (
                        <>
                          <button
                            onClick={() => handleMove(idx, 'up')}
                            disabled={ownIdx === 0}
                            className="text-slate-300 hover:text-slate-500 disabled:opacity-30 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Move up"
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleMove(idx, 'down')}
                            disabled={ownIdx === ownRows.length - 1}
                            className="text-slate-300 hover:text-slate-500 disabled:opacity-30 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Move down"
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleToggle(s)}
                            className={`opacity-0 transition-opacity group-hover:opacity-100 ${s.isActive ? 'text-emerald-500 hover:text-emerald-600' : 'text-slate-300 hover:text-slate-400'}`}
                            title={s.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {s.isActive ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                          </button>
                          <button
                            onClick={() => { setEditId(s.id); setEditName(s.name); setEditColor(s.color); }}
                            className="text-slate-400 opacity-0 transition-opacity hover:text-[#6366F1] group-hover:opacity-100"
                            title="Edit"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            onClick={() => handleDelete(s.id)}
                            className="text-slate-400 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
                );
              })
            )}
          </div>
        </div>
        <p className="text-xs text-slate-400">{statuses.length} status{statuses.length !== 1 ? 'es' : ''}</p>
      </CardContent>
    </Card>
  );
}

// ── Lead Sources Tab ─────────────────────────────────────────────────
//
// Lists the merged set of platform-default sources (super-admin owned,
// read-only here) and the tenant's own custom sources (full CRUD).
// Backend endpoint /leads/sources already does the merge + tagging.

interface LeadSourceRow {
  id: string;
  name: string;
  color?: string;
  isActive: boolean;
  source: 'system' | 'own';
}

function LeadSourcesTab() {
  const [sources, setSources] = useState<LeadSourceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addColor, setAddColor] = useState(STATUS_PRESET_COLORS[0]);
  const [adding, setAdding] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editColor, setEditColor] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchSources = () => {
    setLoading(true);
    api.get('/leads/sources')
      .then(({ data }) => setSources(Array.isArray(data) ? data : []))
      .catch(() => toast('error', 'Failed to load sources'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchSources(); }, []);

  const handleAdd = async () => {
    if (!addName.trim()) return;
    setAdding(true);
    try {
      await api.post('/leads/sources', { name: addName.trim(), color: addColor });
      setAddName('');
      setAddColor(STATUS_PRESET_COLORS[0]);
      setShowAdd(false);
      fetchSources();
      toast('success', 'Source added');
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to add source');
    } finally {
      setAdding(false);
    }
  };

  const startEdit = (s: LeadSourceRow) => {
    setEditId(s.id);
    setEditName(s.name);
    setEditColor(s.color || STATUS_PRESET_COLORS[0]);
  };

  const handleSaveEdit = async (id: string) => {
    if (!editName.trim()) return;
    setSaving(true);
    try {
      await api.patch(`/leads/sources/${id}`, { name: editName.trim(), color: editColor });
      setEditId(null);
      fetchSources();
      toast('success', 'Source updated');
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to update source');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (s: LeadSourceRow) => {
    try {
      await api.patch(`/leads/sources/${s.id}`, { isActive: !s.isActive });
      fetchSources();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to toggle');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/leads/sources/${id}`);
      fetchSources();
      toast('success', 'Source deleted');
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete source');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base">Lead Sources</CardTitle>
            <CardDescription>
              Platform defaults + your custom sources. Platform defaults are read-only.
            </CardDescription>
          </div>
          <Button
            size="sm"
            onClick={() => setShowAdd(true)}
            className="h-8 bg-[#6366F1] px-3 text-white hover:bg-[#5254cc]"
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" /> Add Source
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {showAdd && (
          <div className="flex items-center gap-3 rounded-lg border border-dashed border-slate-200 bg-slate-50/50 p-3">
            <Input
              placeholder="Source name..."
              value={addName}
              onChange={(e) => setAddName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              className="h-8 flex-1 text-sm"
              autoFocus
            />
            <div className="flex gap-1">
              {STATUS_PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setAddColor(c)}
                  className={`h-6 w-6 rounded-full border-2 transition-all ${addColor === c ? 'border-slate-800 scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
            <Button size="sm" onClick={handleAdd} disabled={adding || !addName.trim()} className="h-8 bg-[#6366F1] text-white hover:bg-[#5254cc]">
              {adding ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
            </Button>
            <button onClick={() => { setShowAdd(false); setAddName(''); }} className="text-slate-400 hover:text-slate-600">
              <X className="h-4 w-4" />
            </button>
          </div>
        )}

        <div className="overflow-hidden rounded-md border border-slate-100">
          <div className="divide-y divide-slate-100">
            {sources.length === 0 ? (
              <p className="py-6 text-center text-sm text-slate-400">No sources configured</p>
            ) : (
              sources.map((s) => {
                const isSystem = s.source === 'system';
                const editing = editId === s.id;
                return (
                  <div key={s.id} className="group flex items-center gap-3 bg-white px-3 py-2.5 hover:bg-slate-50">
                    {editing && !isSystem ? (
                      <>
                        <div className="flex gap-1 flex-shrink-0">
                          {STATUS_PRESET_COLORS.map((c) => (
                            <button
                              key={c}
                              type="button"
                              onClick={() => setEditColor(c)}
                              className={`h-5 w-5 rounded-full border-2 transition-all ${editColor === c ? 'border-slate-800 scale-110' : 'border-transparent'}`}
                              style={{ backgroundColor: c }}
                            />
                          ))}
                        </div>
                        <Input
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveEdit(s.id);
                            if (e.key === 'Escape') setEditId(null);
                          }}
                          className="h-7 flex-1 text-sm"
                          autoFocus
                        />
                        <button onClick={() => handleSaveEdit(s.id)} className="text-emerald-600 hover:text-emerald-700" disabled={saving}>
                          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        </button>
                        <button onClick={() => setEditId(null)} className="text-slate-400 hover:text-slate-600">
                          <X className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <>
                        <span
                          className="h-3 w-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: s.color || '#6366F1' }}
                        />
                        <span className="flex-1 text-sm text-slate-800 truncate">
                          {s.name}
                        </span>
                        {isSystem ? (
                          <span
                            className="inline-flex items-center gap-1 text-[10px] font-semibold bg-slate-100 text-slate-600 px-1.5 py-0.5 rounded"
                            title="Platform default — read-only"
                          >
                            🔒 Default
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-[10px] font-semibold bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded">
                            Custom
                          </span>
                        )}

                        {!isSystem && (
                          <button
                            type="button"
                            onClick={() => handleToggle(s)}
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors flex-shrink-0 ${s.isActive ? 'bg-[#6366F1]' : 'bg-slate-200'}`}
                            title={s.isActive ? 'Active' : 'Inactive'}
                          >
                            <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow transition-transform ${s.isActive ? 'translate-x-5' : 'translate-x-0.5'}`} />
                          </button>
                        )}

                        {!isSystem && (
                          <>
                            <button
                              onClick={() => startEdit(s)}
                              className="text-slate-400 hover:text-[#6366F1]"
                              title="Edit"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              onClick={() => handleDelete(s.id)}
                              className="text-slate-400 hover:text-red-500"
                              title="Delete"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </>
                        )}
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function CustomFieldsTab() {
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  const [drawer, setDrawer] = useState<{ open: boolean; field?: CustomField }>({ open: false });
  const [confirmField, setConfirmField] = useState<CustomField | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try { const { data } = await api.get('/settings/custom-fields'); setFields(data); }
    catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load custom fields');
    } finally { setLoading(false); }
  };

  useEffect(() => { fetch(); }, []);

  const handleDelete = (field: CustomField) => setConfirmField(field);

  const handleConfirmDelete = async () => {
    if (!confirmField) return;
    setConfirmLoading(true);
    try {
      await api.delete(`/settings/custom-fields/${confirmField.id}`);
      setConfirmField(null);
      fetch();
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete');
    } finally {
      setConfirmLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div><h2 className="text-lg font-semibold text-slate-900">Custom Fields</h2>
          <p className="text-sm text-slate-500 mt-0.5">Add custom fields to leads and users</p>
        </div>
        <Button onClick={() => setDrawer({ open: true })}><Plus className="h-4 w-4 mr-1.5" />Add Field</Button>
      </div>
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr className="border-b-2 border-slate-200">
                {['Entity', 'Label', 'Type', 'Order', 'Required', 'Status', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {loading ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center"><Loader2 className="h-5 w-5 animate-spin text-indigo-500 mx-auto" /></td></tr>
              ) : fields.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-400 text-sm">No custom fields yet. Add your first field.</td></tr>
              ) : fields.map(f => (
                <tr key={f.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3"><span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">{f.entityType}</span></td>
                  <td className="px-4 py-3 font-medium text-slate-800">{f.label}</td>
                  <td className="px-4 py-3 text-slate-500 capitalize">{f.fieldType}</td>
                  <td className="px-4 py-3 text-slate-500 text-center">{f.sortOrder}</td>
                  <td className="px-4 py-3 text-center">{f.isRequired ? <Check className="h-4 w-4 text-emerald-500 mx-auto" /> : <span className="block h-4 w-4 mx-auto border border-gray-200 rounded" />}</td>
                  <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${f.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>{f.isActive ? 'Active' : 'Inactive'}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => setDrawer({ open: true, field: f })} className="p-1.5 rounded hover:bg-gray-100 text-slate-400 hover:text-indigo-600"><Pencil className="h-3.5 w-3.5" /></button>
                      <button onClick={() => handleDelete(f)} className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500"><Trash2 className="h-3.5 w-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {drawer.open && <CFDrawer field={drawer.field} onClose={() => setDrawer({ open: false })} onSaved={() => { setDrawer({ open: false }); fetch(); }} />}
      <ConfirmModal
        isOpen={!!confirmField}
        title="Delete Field?"
        message={`Are you sure you want to delete the field "${confirmField?.label}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmField(null)}
        isLoading={confirmLoading}
      />
    </div>
  );
}

// ── Integrations Tab ──────────────────────────────────────────────────────────

function PwdInput({ id, value, onChange, placeholder, showMap, onToggle, onReveal, inputRef }: {
  id: string; value: string; onChange: (v: string) => void;
  placeholder?: string; showMap: Record<string, boolean>; onToggle: (id: string) => void;
  // When the field still holds the server's masked placeholder
  // ('************…'), clicking the eye fires onReveal(id) — the parent
  // hits the integrations/:section/:field/reveal endpoint, returns the
  // decrypted plaintext, and we swap the masked value for it before
  // flipping the input to text mode. If onReveal isn't provided or
  // returns null, we fall back to a plain show/hide toggle.
  onReveal?: (id: string) => Promise<string | null>;
  // Optional ref forwarded to the underlying <input> so the parent can
  // focus it programmatically (e.g. when clicking the eye on a saved-but-
  // empty field the eye focuses the input as a "paste here" cue).
  inputRef?: React.RefObject<HTMLInputElement>;
}) {
  const [revealing, setRevealing] = useState(false);
  const isMasked = typeof value === 'string' && value.startsWith('************');

  const handleEyeClick = async () => {
    // Hide: already visible → toggle back to password mode.
    if (showMap[id]) {
      onToggle(id);
      return;
    }
    // Reveal from server: field holds a server-masked placeholder and a
    // reveal endpoint is wired up → fetch real value then show it.
    if (isMasked && onReveal) {
      setRevealing(true);
      try {
        const real = await onReveal(id);
        if (real != null) onChange(real);
      } finally {
        setRevealing(false);
      }
      onToggle(id);
      return;
    }
    // Masked but no reveal endpoint (e.g. WhatsApp / platform Meta App
    // secret): the value IS the mask string, so showing it as text is
    // useless. Clear the field instead so the user can type a new value.
    if (isMasked) {
      onChange('');
      return;
    }
    // Field is empty: if a reveal function is wired up, fetch the real
    // value from the server (e.g. WA token decrypt endpoint). Otherwise
    // just focus the input as a "paste new value here" cue.
    if (!value) {
      if (onReveal) {
        setRevealing(true);
        try {
          const real = await onReveal(id);
          if (real != null) {
            onChange(real);
            onToggle(id); // switch to text mode so the revealed value is visible
          }
        } finally {
          setRevealing(false);
        }
        return;
      }
      inputRef?.current?.focus();
      return;
    }
    onToggle(id);
  };

  return (
    <div className="relative">
      <Input
        ref={inputRef}
        type={showMap[id] ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder ?? '••••••••'}
        className="pr-9"
        // Kill browser autofill/saved-value suggestions on secret fields —
        // a pre-filled token/API key confuses vendors about what to save.
        autoComplete="new-password"
        autoCorrect="off"
        autoCapitalize="off"
        spellCheck={false}
      />
      <button
        type="button"
        onClick={handleEyeClick}
        disabled={revealing}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 disabled:opacity-50"
        title={
          showMap[id] ? 'Hide'
          : isMasked && onReveal ? 'Reveal saved value'
          : isMasked ? 'Clear to re-enter'
          : !value && onReveal ? 'Reveal saved token'
          : !value ? 'Click to update token'
          : 'Show'
        }
      >
        {revealing
          ? <Loader2 className="h-4 w-4 animate-spin" />
          : showMap[id] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function IntegrationStatusBadge({ connected }: { connected: boolean }) {
  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${connected ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
      {connected ? 'Connected' : 'Not Connected'}
    </span>
  );
}

// ── Workspace Tab (tenant-level preferences) ─────────────────────────────
//
// Currently exposes:
//   - Tenant ID (readonly)
//   - Lead Assignment Mode: individual | team | hybrid
//
// Mode controls how LeadService routes incoming leads:
//   individual → only per-user LeadAssignRule rules (current behavior, default)
//   team       → only team-scoped rules; round-robin within team members
//   hybrid     → team rules first, fall back to individual rules

type AssignmentMode = 'individual' | 'team' | 'hybrid';

function WorkspaceTab({ tenantId }: { tenantId: string }) {
  const [mode, setMode] = useState<AssignmentMode>('individual');
  const [autoMerge, setAutoMerge] = useState(true);
  const [sla, setSla] = useState(60);
  const [savingSla, setSavingSla] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingMerge, setSavingMerge] = useState(false);
  const [showTenantId, setShowTenantId] = useState(false);

  useEffect(() => {
    api.get('/settings')
      .then(({ data }) => {
        setMode((data?.leadAssignmentMode as AssignmentMode) ?? 'individual');
        // Default ON — only false explicitly disables it.
        setAutoMerge(data?.autoMergeDuplicates !== false);
        setSla(Number(data?.speedToLead?.slaSeconds) || 60);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const saveMode = async (next: AssignmentMode) => {
    const previous = mode;
    setMode(next); // optimistic
    setSaving(true);
    try {
      await api.patch('/settings/lead-assignment-mode', { mode: next });
      toast('success', 'Lead assignment mode updated');
    } catch (err: any) {
      setMode(previous);
      toast('error', err?.response?.data?.message || 'Failed to update mode');
    } finally {
      setSaving(false);
    }
  };

  const saveAutoMerge = async (next: boolean) => {
    const previous = autoMerge;
    setAutoMerge(next); // optimistic
    setSavingMerge(true);
    try {
      await api.patch('/settings/auto-merge-duplicates', { enabled: next });
      toast('success', next ? 'Auto-merge enabled' : 'Auto-merge disabled');
    } catch (err: any) {
      setAutoMerge(previous);
      toast('error', err?.response?.data?.message || 'Failed to update setting');
    } finally {
      setSavingMerge(false);
    }
  };

  const saveSla = async (next: number) => {
    const previous = sla;
    setSla(next); // optimistic
    setSavingSla(true);
    try {
      await api.patch('/settings/speed-to-lead', { slaSeconds: next });
      toast('success', 'Speed-to-lead target updated');
    } catch (err: any) {
      setSla(previous);
      toast('error', err?.response?.data?.message || 'Failed to update target');
    } finally {
      setSavingSla(false);
    }
  };

  const options: { value: AssignmentMode; title: string; description: string }[] = [
    {
      value: 'individual',
      title: 'Individual',
      description: 'Leads distribute equally among all counselors via round-robin. Default.',
    },
    {
      value: 'team',
      title: 'Team-Based',
      description: 'Leads route to specific teams based on rules; round-robin within team members.',
    },
    {
      value: 'hybrid',
      title: 'Hybrid',
      description: 'Try team routing first. If no team rule matches, fall back to individual round-robin.',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Workspace</CardTitle>
        <CardDescription>Manage workspace-level preferences and lead assignment strategy.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>Tenant ID</Label>
          <div className="relative">
            <Input
              value={showTenantId ? tenantId : '•'.repeat(Math.max(tenantId.length, 16))}
              disabled
              className="bg-slate-50 font-mono text-xs pr-16"
            />
            <div className="absolute inset-y-0 right-1.5 flex items-center gap-0.5">
              <button
                type="button"
                onClick={() => setShowTenantId((v) => !v)}
                className="p-1.5 rounded hover:bg-slate-200 text-slate-500"
                title={showTenantId ? 'Hide' : 'Show'}
              >
                {showTenantId ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(tenantId); toast('success', 'Tenant ID copied'); }}
                className="p-1.5 rounded hover:bg-slate-200 text-slate-500"
                title="Copy"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
          </div>
          <p className="text-xs text-slate-400">
            Public identifier — safe to share. Used to embed your lead-capture form. This is not a password.
          </p>
        </div>

        <div className="space-y-2 pt-2 border-t border-slate-100">
          <div className="flex items-center justify-between">
            <div>
              <Label>Lead Assignment Mode</Label>
              <p className="text-xs text-slate-500 mt-1">
                How incoming leads are routed to counselors.
              </p>
            </div>
            {saving && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-indigo-500" />
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-2 pt-2">
              {options.map((opt) => (
                <label
                  key={opt.value}
                  className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-colors ${
                    mode === opt.value
                      ? 'border-blue-400 bg-blue-50/50'
                      : 'border-slate-200 hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="assignmentMode"
                    checked={mode === opt.value}
                    onChange={() => saveMode(opt.value)}
                    disabled={saving}
                    className="mt-0.5 accent-blue-600"
                  />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{opt.title}</p>
                    <p className="text-xs text-slate-500 mt-1">{opt.description}</p>
                  </div>
                </label>
              ))}
            </div>
          )}

          {mode !== 'individual' && (
            <p className="text-xs text-amber-600 pt-2">
              ⓘ Make sure you've created Teams (Administrator → Teams) and assigned them to
              your Lead Assign rules before saving leads through this mode.
            </p>
          )}
        </div>

        {/* Auto-merge duplicate leads */}
        <div className="space-y-2 pt-4 border-t border-slate-100">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label>Auto-merge duplicate leads</Label>
              <p className="text-xs text-slate-500 mt-1">
                When a new lead matches an existing one by phone or email, merge it into
                the original record instead of creating a duplicate. The original keeps
                its status and assignment; missing fields get backfilled from the new
                entry. Turn off to allow duplicate rows (you'll see a notification).
              </p>
            </div>
            <button
              type="button"
              onClick={() => saveAutoMerge(!autoMerge)}
              disabled={savingMerge || loading}
              className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                autoMerge ? 'bg-indigo-600' : 'bg-slate-300'
              } ${(savingMerge || loading) ? 'opacity-60 cursor-not-allowed' : ''}`}
              aria-pressed={autoMerge}
              aria-label="Auto-merge duplicate leads"
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  autoMerge ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          {savingMerge && (
            <p className="text-xs text-slate-400">
              <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
              Saving…
            </p>
          )}
        </div>

        {/* Speed-to-lead SLA target */}
        <div className="space-y-2 pt-4 border-t border-slate-100">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <Label>Speed-to-lead target</Label>
              <p className="text-xs text-slate-500 mt-1">
                How fast a newly assigned lead should get its first response. The
                lead's SLA badge stays amber until then and turns red past it.
                A faster target drives higher conversion.
              </p>
            </div>
            <SelectField
              value={sla}
              onChange={e => saveSla(Number(e.target.value))}
              disabled={savingSla || loading}
              className="h-9 flex-shrink-0 rounded-lg border border-slate-200 bg-white px-2 text-sm disabled:opacity-60"
            >
              <option value={30}>30 seconds</option>
              <option value={60}>1 minute</option>
              <option value={120}>2 minutes</option>
              <option value={300}>5 minutes</option>
              <option value={600}>10 minutes</option>
              <option value={1800}>30 minutes</option>
            </SelectField>
          </div>
          {savingSla && (
            <p className="text-xs text-slate-400">
              <Loader2 className="inline h-3 w-3 animate-spin mr-1" />
              Saving…
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

// IntegrationsTab is exported and reused in two places:
// 1. /settings (vendor) — uses the regular `api` axios instance with
//    /settings/integrations endpoints and the per-tenant Google/Zoom
//    OAuth flow on /availability/integrations/*.
// 2. /super-admin/settings (platform owner) — uses `saApi` with
//    /super-admin/settings/integrations and skips the OAuth controls
//    because there's no per-tenant context to bind the OAuth state to
//    at the platform level (admins still configure the clientId +
//    clientSecret pair for tenants to inherit defaults from).
//
// `mode='platform'` hides the per-tenant OAuth Connect / Disconnect /
// Test buttons and the test-email helper, both of which are tenant-only.
export interface IntegrationsTabProps {
  apiClient?: AxiosInstance;
  endpoint?: string;
  mode?: 'tenant' | 'platform';
  /** When provided (super-admin context), WhatsApp account operations
   *  use this base path instead of /whatsapp to bypass TenantGuard. */
  whatsappAccountsBase?: string;
}

export function IntegrationsTab({
  apiClient = api,
  endpoint = '/settings/integrations',
  mode = 'tenant',
  whatsappAccountsBase,
}: IntegrationsTabProps = {}) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [showPwd, setShowPwd] = useState<Record<string, boolean>>({});
  const togglePwd = (id: string) => setShowPwd(p => ({ ...p, [id]: !p[id] }));

  const [fb, setFb] = useState({ appId: '', appSecret: '', verifyToken: '', pageId: '', pageName: '', accessToken: '' });
  const [gg, setGg] = useState({ clientId: '', clientSecret: '', developerToken: '' });
  const [wa, setWa] = useState({ phoneNumberId: '', accessToken: '', webhookVerifyToken: '' });
  const [metaApp, setMetaApp] = useState({ metaAppId: '', metaAppSecret: '', metaConfigId: '', webhookVerifyToken: '', metaAppSecretSet: false, source: '', secretTouched: false, verifyTokenTouched: false });
  const [savingMetaApp, setSavingMetaApp] = useState(false);

  // ── WhatsApp connect wizard state ────────────────────────────────────
  const [waMigrating, setWaMigrating] = useState(false);
  const [waMigrationNeedsWaba, setWaMigrationNeedsWaba] = useState<{ phoneNumberId: string } | null>(null);
  const [waMigrationWabaId, setWaMigrationWabaId] = useState('');
  // waAccounts: list from GET /whatsapp/accounts
  // waConnectMethod: which tab the vendor picked
  // waStep: idle → (popup flow) pin | (token flow) token | connected
  // waPopupData: code + phone/waba ids captured from the FB popup message event
  const [waAccounts, setWaAccounts] = useState<any[]>([]);
  // accountId → list of APPROVED templates for that account
  const [waApprovedTemplates, setWaApprovedTemplates] = useState<Record<string, any[]>>({});
  const [waConnectMethod, setWaConnectMethod] = useState<'quick' | 'token' | 'wati' | 'gupshup' | 'advanced'>('token');
  const [waStep, setWaStep] = useState<'idle' | 'pin' | 'connecting'>('idle');
  const [waPopupData, setWaPopupData] = useState<{ code: string; phoneNumberId: string; wabaId: string } | null>(null);
  const [waPin, setWaPin] = useState('');
  const [waTokenForm, setWaTokenForm] = useState({ phoneNumberId: '', wabaId: '', accessToken: '' });
  const waTokenInputRef = useRef<HTMLInputElement>(null);
  // Card accordion state for the new provider-card layout
  const [waOpenCard, setWaOpenCard] = useState<'meta' | 'wati' | 'gupshup' | null>('meta');
  const [waMetaShowToken, setWaMetaShowToken] = useState(true); // "Advanced" token form inside Meta card (expanded by default)
  const [waShowGuide, setWaShowGuide] = useState(false);         // Step-by-step WhatsApp setup guide inside Meta card
  const [waAdvancedOpen, setWaAdvancedOpen] = useState(false);   // Advanced Meta App config section
  const [fbAdvanced, setFbAdvanced] = useState(false);           // Advanced: vendor's own Meta App
  const [waWatiForm, setWaWatiForm] = useState({ apiEndpoint: '', apiKey: '' });
  const [waGupshupForm, setWaGupshupForm] = useState({ apiKey: '', appName: '' });
  const [waConnecting, setWaConnecting] = useState(false);
  const [waActivating, setWaActivating] = useState(false); // switching the single active connection
  const [waEmbeddedConfig, setWaEmbeddedConfig] = useState<{ appId: string | null; configId: string | null; available: boolean }>({ appId: null, configId: null, available: false })
  const [waWebhookConfig, setWaWebhookConfig] = useState<{ webhookUrl: string; verifyToken: string | null; verifyTokenSet: boolean }>({ webhookUrl: 'https://api.brixlead.com/api/webhooks/whatsapp', verifyToken: null, verifyTokenSet: false });
  const [email, setEmail] = useState({ provider: 'sendgrid', apiKey: '', domain: '', host: '', port: '587', username: '', password: '', fromName: '', fromEmail: '' });
  const [sms, setSms] = useState({ provider: 'twilio', accountSid: '', authToken: '', fromNumber: '', apiKey: '', apiSecret: '', sid: '', authKey: '', senderId: '' });
  const [gcal, setGcal] = useState({ clientId: '', clientSecret: '' });
  const [zoom, setZoom] = useState({ accountId: '', clientId: '', clientSecret: '', hostEmail: '', webhookSecretToken: '' });
  // Platform-default AI voice provider (super-admin) — tenants fall back to this.
  const [voice, setVoice] = useState({ provider: 'retell', apiKey: '', fromNumber: '', elevenLabsPhoneNumberId: '', defaultAgentId: '' });
  const [gcalStatus, setGcalStatus] = useState<{ connected: boolean; email?: string; platformConfigured?: boolean }>({ connected: false });
  const [zoomStatus, setZoomStatus] = useState<{ connected: boolean; email?: string }>({ connected: false });
  const [fbStatus, setFbStatus] = useState<{ connected: boolean; pageName?: string; pageId?: string; platformAvailable?: boolean }>({ connected: false });
  const [testingEmail, setTestingEmail] = useState(false);

  useEffect(() => {
    const calls: Promise<unknown>[] = [
      apiClient.get(endpoint).then(({ data }) => {
        if (data.facebook) setFb(p => ({
          ...p,
          appId: data.facebook.appId || '',
          appSecret: data.facebook.appSecret || '',
          verifyToken: data.facebook.verifyToken || '',
          pageId: data.facebook.pageId || '',
          pageName: data.facebook.pageName || '',
          accessToken: data.facebook.accessToken || '',
        }));
        if (data.google) setGg(p => ({ ...p, ...data.google }));
        if (data.whatsapp) setWa(p => ({ ...p, ...data.whatsapp }));
        if (data.email) setEmail(p => ({ ...p, ...data.email }));
        if (data.sms) setSms(p => ({ ...p, ...data.sms }));
        if (data.calendar) setGcal(p => ({ ...p, clientId: data.calendar.clientId || '', clientSecret: data.calendar.clientSecret || '' }));
        if (data.zoom) setZoom(p => ({ ...p, accountId: data.zoom.accountId || '', clientId: data.zoom.clientId || '', clientSecret: data.zoom.clientSecret || '', hostEmail: data.zoom.hostEmail || '', webhookSecretToken: data.zoom.webhookSecretToken || '' }));
        if (data.voice) setVoice(p => ({ ...p, provider: data.voice.provider || 'retell', apiKey: data.voice.apiKey || '', fromNumber: data.voice.fromNumber || '', elevenLabsPhoneNumberId: data.voice.elevenLabsPhoneNumberId || '', defaultAgentId: data.voice.defaultAgentId || '' }));
      }),
    ];
    // OAuth status probes — mode-aware. Tenant uses /availability/* (per-
    // tenant record), platform uses /super-admin/integrations/* (singleton
    // PlatformSetting).
    const oauthBase = mode === 'tenant' ? '/availability/integrations' : '/super-admin/integrations';
    calls.push(apiClient.get(`${oauthBase}/google/status`).then(r => setGcalStatus(r.data)).catch(() => {}));
    calls.push(apiClient.get(`${oauthBase}/zoom/status`).then(r => setZoomStatus(r.data)).catch(() => {}));
    calls.push(apiClient.get(`${oauthBase}/facebook/status`).then(r => setFbStatus(r.data)).catch(() => {}));
    // WhatsApp: load connected accounts + embedded signup config
    // Tenant mode: use /whatsapp/* endpoints
    // Platform mode + whatsappAccountsBase provided: use super-admin WhatsApp endpoints
    const waBase = mode === 'tenant' ? '/whatsapp' : (whatsappAccountsBase ?? null);
    if (waBase) {
      calls.push(apiClient.get(`${waBase}/accounts`).then(async r => {
        const accounts: any[] = Array.isArray(r.data) ? r.data : [];
        setWaAccounts(accounts);
        // Load APPROVED + PENDING templates for each connected account.
        // APPROVED ones are selectable; PENDING shown greyed-out so the
        // vendor can see what's coming.
        const tplMap: Record<string, any[]> = {};
        // Templates live on the tenant-only /whatsapp/templates route. In
        // platform (super-admin) mode there is no such endpoint, and calling
        // the vendor route with a super-admin token returns 401 — which the
        // saApi interceptor turns into a logout redirect. So skip it here.
        if (mode === 'tenant') {
          await Promise.all(accounts.map(async (acc: any) => {
            try {
              const [approvedRes, pendingRes] = await Promise.all([
                apiClient.get('/whatsapp/templates', { params: { whatsappAccountId: acc.id, status: 'APPROVED', limit: 100 } }),
                apiClient.get('/whatsapp/templates', { params: { whatsappAccountId: acc.id, status: 'PENDING', limit: 100 } }),
              ]);
              const approved = Array.isArray(approvedRes.data?.data) ? approvedRes.data.data : [];
              const pending  = Array.isArray(pendingRes.data?.data)  ? pendingRes.data.data  : [];
              tplMap[acc.id] = [...approved, ...pending];
            } catch { tplMap[acc.id] = []; }
          }));
        }
        setWaApprovedTemplates(tplMap);
        // Pre-populate token form with the most recent account's IDs so
        // the vendor never has to re-type Phone Number ID / WABA ID.
        // They only need to paste a fresh access token on reconnect.
        // Pre-populate Meta Direct form ONLY from META accounts
        // (Wati/Gupshup accounts have phoneNumberId like "wati:..." and null wabaId)
      }).catch(() => {}));
      calls.push(apiClient.get(`${waBase}/onboarding/config`).then(r => setWaEmbeddedConfig(r.data)).catch(() => {}));
      calls.push(apiClient.get(`${waBase}/integration-settings/webhook-config`).then(r => setWaWebhookConfig(r.data)).catch(() => {}));
    }
    // Fetch Meta App credentials (tenant or platform depending on mode)
    const metaAppEndpoint = mode === 'tenant'
      ? '/whatsapp/integration-settings'
      : '/super-admin/platform-settings/whatsapp';
    calls.push(
      apiClient.get(metaAppEndpoint)
        .then(({ data }) => setMetaApp(p => ({
          ...p,
          metaAppId: data.metaAppId ?? '',
          metaConfigId: data.metaConfigId ?? '',
          metaAppSecretSet: !!data.metaAppSecretSet,
          webhookVerifyToken: data.webhookVerifyToken ?? '',
          source: data.source ?? '',
          secretTouched: false,
          verifyTokenTouched: false,
        })))
        .catch(() => {}),
    );
    Promise.all(calls).catch(() => {}).finally(() => setLoading(false));
  }, [apiClient, endpoint, mode, whatsappAccountsBase]);

  // Keep Meta Direct form in sync with the DB.
  // Runs whenever waAccounts changes (initial load + after every connect/disconnect).
  // DB values always win for phoneNumberId and wabaId — user only needs to re-enter
  // the access token (sensitive, never stored on frontend).
  useEffect(() => {
    const metaAcc = waAccounts.find(
      (a: any) => !a.provider || a.provider === 'META',
    );
    if (metaAcc) {
      setWaTokenForm(p => ({
        ...p,
        phoneNumberId: metaAcc.phoneNumberId || p.phoneNumberId,
        wabaId: metaAcc.wabaId || p.wabaId || '',
      }));
    }
  }, [waAccounts]);

  const saveMetaApp = async () => {
    setSavingMetaApp(true);
    try {
      const metaAppEndpoint = mode === 'tenant'
        ? '/whatsapp/integration-settings'
        : '/super-admin/platform-settings/whatsapp';
      const body: Record<string, string | null> = {
        metaAppId: metaApp.metaAppId || null,
        metaConfigId: metaApp.metaConfigId || null,
      };
      if (metaApp.secretTouched) {
        body.metaAppSecret = metaApp.metaAppSecret || null;
      }
      if (metaApp.verifyTokenTouched) {
        body.webhookVerifyToken = metaApp.webhookVerifyToken || null;
      }
      const { data } = await apiClient.patch(metaAppEndpoint, body);
      setMetaApp(p => ({
        ...p,
        metaAppId: data.metaAppId ?? '',
        metaConfigId: data.metaConfigId ?? '',
        metaAppSecretSet: !!data.metaAppSecretSet,
        webhookVerifyToken: data.webhookVerifyToken ?? '',
        source: data.source ?? '',
        metaAppSecret: '',
        secretTouched: false,
        verifyTokenTouched: false,
      }));
      toast('success', 'Meta App credentials saved');
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to save Meta App credentials');
    } finally {
      setSavingMetaApp(false);
    }
  };

  const saveSection = async (section: string, data: Record<string, any>) => {
    setSaving(p => ({ ...p, [section]: true }));
    try {
      await apiClient.patch(endpoint, { [section]: data });
      toast('success', 'Integration settings saved');
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(p => ({ ...p, [section]: false }));
    }
  };

  // Reveal a single sensitive field on demand. PwdInput ids look like
  // 'zoom_clientSecret' / 'sms_authToken' / 'email_apiKey' — split on the
  // first underscore to get (section, field) and hit the mode-appropriate
  // /reveal endpoint. Backend returns the decrypted plaintext.
  const revealField = async (id: string): Promise<string | null> => {
    const idx = id.indexOf('_');
    if (idx === -1) return null;
    const section = id.slice(0, idx);
    const field = id.slice(idx + 1);
    // Map UI section keys to backend section keys.
    const sectionMap: Record<string, string> = {
      fb: 'facebook',
      gg: 'google',
      wa: 'whatsapp',
      gcal: 'calendar',
      sms: 'sms',
      email: 'email',
      zoom: 'zoom',
    };
    const backendSection = sectionMap[section] || section;
    const base = endpoint.replace(/\/integrations$/, '/integrations');
    try {
      const { data } = await apiClient.get(`${base}/${backendSection}/${field}/reveal`);
      return typeof data?.value === 'string' ? data.value : null;
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to reveal');
      return null;
    }
  };

  // Reveal the decrypted WhatsApp access token for a connected account.
  // The backend decrypts it server-side and returns the plaintext — used by
  // the eye icon on the Permanent Access Token field in Settings.
  const revealWaToken = async (_id: string): Promise<string | null> => {
    const acc = waAccounts.find(
      (a: any) => a.phoneNumberId === waTokenForm.phoneNumberId && a.status === 'CONNECTED',
    );
    if (!acc) return null;
    try {
      const { data } = await apiClient.get(`${waApiBase}/accounts/${acc.id}/reveal-token`);
      return typeof data?.token === 'string' ? data.token : null;
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to reveal token');
      return null;
    }
  };

  const testEmailConnection = async () => {
    if (mode !== 'tenant') return;   // platform mode has no test endpoint yet
    setTestingEmail(true);
    try {
      await apiClient.post('/settings/integrations/test-email', { email });
      toast('success', 'Connection successful');
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Connection failed. Check your credentials.');
    } finally {
      setTestingEmail(false);
    }
  };

  // ── Google Calendar / Zoom OAuth handlers ───────────────────────────────
  const [testingOauth, setTestingOauth] = useState<'google' | 'zoom' | 'facebook' | null>(null);
  const [disconnectConfirm, setDisconnectConfirm] = useState<'google' | 'zoom' | 'facebook' | null>(null);
  const [disconnectLoading, setDisconnectLoading] = useState(false);

  // Mode-aware OAuth endpoint base — tenant uses the per-tenant
  // /availability/integrations routes, platform (super-admin) uses the
  // singleton /super-admin/integrations routes that read/write the
  // PlatformSetting row.
  const oauthBase = mode === 'tenant' ? '/availability/integrations' : '/super-admin/integrations';

  const handleOauthConnect = async (provider: 'google' | 'zoom' | 'facebook') => {
    try {
      const { data } = await apiClient.get(`${oauthBase}/${provider}/connect`);
      if (data.url) {
        window.open(data.url, '_blank', 'width=600,height=700');
      } else if (data.error) {
        toast('error', data.error);
      }
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to start connection');
    }
  };

  const handleOauthTest = async (provider: 'google' | 'zoom' | 'facebook') => {
    setTestingOauth(provider);
    try {
      const { data } = await apiClient.post(`${oauthBase}/${provider}/test`);
      if (data?.ok) {
        toast('success', `Connection OK${data.email ? ` (${data.email})` : ''}`);
      } else {
        toast('error', data?.error || 'Connection test failed');
      }
    } catch (err: any) {
      toast('error', err?.response?.data?.error || err?.response?.data?.message || 'Connection test failed');
    } finally {
      setTestingOauth(null);
    }
  };

  const handleOauthDisconnect = (provider: 'google' | 'zoom' | 'facebook') => {
    setDisconnectConfirm(provider);
  };

  const confirmOauthDisconnect = async () => {
    if (!disconnectConfirm) return;
    const provider = disconnectConfirm;
    setDisconnectLoading(true);
    try {
      await apiClient.delete(`${oauthBase}/${provider}/disconnect`);
      if (provider === 'google') setGcalStatus({ connected: false });
      else if (provider === 'zoom') setZoomStatus({ connected: false });
      else setFbStatus({ connected: false });
      const label = provider === 'google' ? 'Google Calendar' : provider === 'zoom' ? 'Zoom' : 'Facebook';
      toast('success', `${label} disconnected`);
      setDisconnectConfirm(null);
    } catch {
      toast('error', 'Failed to disconnect');
    } finally {
      setDisconnectLoading(false);
    }
  };

  const refreshOauthStatus = async (provider: 'google' | 'zoom' | 'facebook') => {
    try {
      const { data } = await apiClient.get(`${oauthBase}/${provider}/status`);
      if (provider === 'google') setGcalStatus(data);
      else if (provider === 'zoom') setZoomStatus(data);
      else setFbStatus(data);
    } catch (err: any) {
      // No toast — this fires on every window focus, would spam. Just log
      // so Sentry catches it if a particular integration starts 5xx-ing.
      console.warn(`OAuth status refresh failed for ${provider}:`, err?.message);
    }
  };

  // Refresh status when user returns from OAuth popup (window focus event)
  useEffect(() => {
    const onFocus = () => {
      refreshOauthStatus('google');
      refreshOauthStatus('zoom');
      refreshOauthStatus('facebook');
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const isConnectedGg = !!(gg.clientId && gg.clientSecret && gg.developerToken);
  // Tenant mode: connected = at least one CONNECTED account in the list
  // Platform mode: keep the old field-based check (admin saves credentials, doesn't use the accounts list)
  const isConnectedWa = (mode === 'tenant' || whatsappAccountsBase)
    ? waAccounts.some(a => a.status === 'CONNECTED')
    : !!(wa.phoneNumberId && wa.accessToken && wa.webhookVerifyToken);
  const isConnectedEmail = email.provider === 'smtp'
    ? !!(email.host && email.port && email.username && email.password && email.fromEmail)
    : !!(email.apiKey && email.fromEmail);
  const isConnectedSms = sms.provider === 'twilio'
    ? !!(sms.accountSid && sms.authToken && sms.fromNumber)
    : sms.provider === 'exotel'
    ? !!(sms.apiKey && sms.apiSecret && sms.sid && sms.fromNumber)
    : !!(sms.authKey && sms.senderId);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggle = (key: string) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  // Resolved base path for WhatsApp account API calls.
  // Tenant mode → /whatsapp, Platform mode with whatsappAccountsBase → that path.
  const waApiBase = mode === 'tenant' ? '/whatsapp' : (whatsappAccountsBase ?? '/whatsapp');

  // Make one connection the single active one (deactivates the others).
  const activateWaAccount = async (id: string) => {
    setWaActivating(true);
    try {
      await apiClient.post(`${waApiBase}/accounts/${id}/activate`);
      const r = await apiClient.get(`${waApiBase}/accounts`);
      setWaAccounts(Array.isArray(r.data) ? r.data : []);
      toast('success', 'Active WhatsApp connection switched');
    } catch (e: any) {
      toast('error', e?.response?.data?.message || 'Could not switch the active connection');
    } finally {
      setWaActivating(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
    </div>
  );

  return (
    <div className="space-y-5">

      {/* ── Card 1: Facebook Lead Ads ── */}
      <Card>
        <CardHeader className="cursor-pointer select-none" onClick={() => toggle('fb')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: '#1877F2' }}>f</div>
              <div>
                <CardTitle className="text-base">Facebook Lead Ads</CardTitle>
                <CardDescription className="text-xs mt-0">Used to automatically capture leads from Facebook Lead Ads campaigns</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <IntegrationStatusBadge connected={fbStatus.connected} />
              {expanded.fb ? <ArrowUp className="h-4 w-4 text-slate-400" /> : <ArrowDown className="h-4 w-4 text-slate-400" />}
            </div>
          </div>
        </CardHeader>
        {expanded.fb && <CardContent className="space-y-4">
          {mode === 'platform' ? (
            /* ── Platform / super-admin view: configure the Meta App credentials
               that all tenants will use for OAuth when they click Connect.     */
            <>
              {fbStatus.connected && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-100">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-emerald-500" />
                    <span className="text-sm text-emerald-700">Platform connected{fbStatus.pageName ? ` to "${fbStatus.pageName}"` : ''}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleOauthTest('facebook')} disabled={testingOauth === 'facebook'}>
                      {testingOauth === 'facebook' && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                      Test
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => handleOauthDisconnect('facebook')}>
                      Disconnect
                    </Button>
                  </div>
                </div>
              )}
              {!fbStatus.connected && (fb.appId && fb.appSecret && fb.verifyToken) && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                    <span className="text-sm text-amber-700">Credentials saved — click Connect to authorize Facebook</span>
                  </div>
                  <Button size="sm" onClick={() => handleOauthConnect('facebook')}>Connect</Button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>App ID</Label>
                  <Input value={fb.appId} onChange={e => setFb(p => ({ ...p, appId: e.target.value }))} placeholder="123456789012345" />
                </div>
                <div className="space-y-1.5">
                  <Label>App Secret</Label>
                  <PwdInput id="fb_appSecret" value={fb.appSecret} onChange={v => setFb(p => ({ ...p, appSecret: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} />
                </div>
                <div className="space-y-1.5 col-span-2">
                  <Label>Verify Token</Label>
                  <PwdInput id="fb_verifyToken" value={fb.verifyToken} onChange={v => setFb(p => ({ ...p, verifyToken: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} placeholder="Random string used by Meta to verify the webhook" />
                </div>
                <div className="space-y-1.5">
                  <Label>Page ID <span className="text-slate-400 font-normal">(optional, auto-set by OAuth)</span></Label>
                  <Input value={fb.pageId} onChange={e => setFb(p => ({ ...p, pageId: e.target.value }))} placeholder="100123456789" />
                </div>
                <div className="space-y-1.5">
                  <Label>Page Access Token <span className="text-slate-400 font-normal">(long-lived / never-expire)</span></Label>
                  <PwdInput id="fb_accessToken" value={fb.accessToken} onChange={v => setFb(p => ({ ...p, accessToken: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} placeholder="Paste your permanent page access token" />
                </div>
              </div>
              <p className="text-xs text-slate-400">
                These credentials are used platform-wide. Vendors connect their own pages via one-click OAuth using this Meta App.
                Webhook URL: <code className="text-indigo-500">https://api.brixlead.com/api/webhooks/facebook-lead</code>.
              </p>
              <div className="flex justify-end">
                <Button onClick={() => saveSection('facebook', fb)} disabled={saving.facebook}>
                  {saving.facebook && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                  Save
                </Button>
              </div>
            </>
          ) : (
            /* ── Tenant / vendor view ── */
            <>
              {/* Connected banner */}
              {fbStatus.connected && (
                <div className="flex items-center justify-between gap-2 px-4 py-3 bg-emerald-50 rounded-xl border border-emerald-100">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium text-emerald-800">
                        Connected{fbStatus.pageName ? ` to "${fbStatus.pageName}"` : ''}
                      </p>
                      <p className="text-xs text-emerald-600 mt-0.5">Leads from your Facebook Lead Ads will be imported automatically</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => handleOauthTest('facebook')} disabled={testingOauth === 'facebook'}>
                      {testingOauth === 'facebook' && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                      Test
                    </Button>
                    <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => handleOauthDisconnect('facebook')}>
                      Disconnect
                    </Button>
                  </div>
                </div>
              )}

              {/* Not connected — one-click connect */}
              {!fbStatus.connected && (
                <div className="flex flex-col items-center gap-4 py-4">
                  <div className="h-16 w-16 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-md" style={{ backgroundColor: '#1877F2' }}>f</div>
                  <div className="text-center max-w-xs">
                    <p className="text-sm font-semibold text-slate-800">Connect your Facebook Page</p>
                    <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                      Connect once and all leads from your Facebook Lead Ads will flow into your CRM automatically.
                    </p>
                  </div>
                  <Button
                    onClick={() => handleOauthConnect('facebook')}
                    className="gap-2 px-6 h-11 font-semibold"
                    style={{ backgroundColor: '#1877F2' }}
                    disabled={!fbStatus.platformAvailable}
                  >
                    <span className="font-bold text-base leading-none">f</span>
                    Connect Facebook Page
                  </Button>
                  {!fbStatus.platformAvailable && (
                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 max-w-xs text-center">
                      Facebook integration is not yet configured by the platform admin. Please contact support.
                    </p>
                  )}
                </div>
              )}

              {/* Advanced: own Meta App — always visible so a vendor can
                  swap to their own app later without disconnecting first.
                  Support asked for this: even after Connect-to-platform,
                  admins wanted the App ID / Secret / Verify Token fields
                  available so they can drop credentials in ahead of a
                  future switch. Previously this whole block was gated on
                  !fbStatus.connected and vanished the moment the vendor
                  hit Connect. */}
              <div className="border-t border-slate-100 pt-3">
                  <button
                    type="button"
                    className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                    onClick={() => setFbAdvanced(v => !v)}
                  >
                    {fbAdvanced ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                    Advanced: Use your own Meta App instead
                  </button>

                  {fbAdvanced && (
                    <div className="mt-4 space-y-4">
                      {/* Credentials saved → connect banner */}
                      {fb.appId && fb.appSecret && fb.verifyToken && (
                        <div className="flex items-center justify-between gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full bg-amber-500" />
                            <span className="text-sm text-amber-700">Credentials saved — click Connect to authorize</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button size="sm" variant="outline" className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={async () => {
                                setFb(p => ({ ...p, appId: '', appSecret: '', verifyToken: '' }));
                                await apiClient.patch(endpoint, { facebook: { appId: '', appSecret: '', verifyToken: '' } });
                                toast('success', 'Credentials cleared');
                              }}>
                              Clear
                            </Button>
                            <Button size="sm" onClick={() => handleOauthConnect('facebook')}>Connect</Button>
                          </div>
                        </div>
                      )}

                      <div className="px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-100">
                        <p className="text-xs text-slate-600 leading-relaxed">
                          Want to use your own Meta App?{' '}
                          Get your App ID, App Secret and Verify Token from{' '}
                          <a href="https://developers.facebook.com" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">Meta for Developers</a>.{' '}
                          Copy the Webhook URL below and add it in your Meta App with a <code className="bg-white px-1 rounded text-[11px]">leadgen</code> subscription.
                        </p>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                          <Label>App ID</Label>
                          <Input autoComplete="off" value={fb.appId} onChange={e => setFb(p => ({ ...p, appId: e.target.value }))} placeholder="123456789012345" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>App Secret</Label>
                          <PwdInput id="fb_appSecret" value={fb.appSecret} onChange={v => setFb(p => ({ ...p, appSecret: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} />
                        </div>
                        <div className="space-y-1.5 col-span-2">
                          <Label>Verify Token</Label>
                          <PwdInput id="fb_verifyToken" value={fb.verifyToken} onChange={v => setFb(p => ({ ...p, verifyToken: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} placeholder="Any random string — must match what you set in Meta webhook" />
                        </div>
                      </div>

                      {/* Webhook URL */}
                      <div className="flex items-center gap-2 px-3 py-2.5 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] text-blue-600 font-semibold mb-0.5">Webhook Callback URL (set this in your Meta App)</p>
                          <code className="text-xs text-blue-700 truncate block">https://api.brixlead.com/api/webhooks/facebook-lead</code>
                        </div>
                        <button
                          type="button"
                          onClick={() => navigator.clipboard.writeText('https://api.brixlead.com/api/webhooks/facebook-lead').then(() => toast('success', 'Copied!'))}
                          className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex-shrink-0 border border-blue-200 rounded px-2 py-1 bg-white"
                        >
                          Copy
                        </button>
                      </div>

                      <div className="flex justify-end">
                        <Button onClick={() => saveSection('facebook', fb)} disabled={saving.facebook}>
                          {saving.facebook && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                          Save
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
            </>
          )}
        </CardContent>}
      </Card>

      {/* ── Card 2: Google Ads ── */}
      <Card>
        <CardHeader className="cursor-pointer select-none" onClick={() => toggle('gg')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg flex items-center justify-center text-white text-sm font-bold flex-shrink-0" style={{ backgroundColor: '#4285F4' }}>G</div>
              <div>
                <CardTitle className="text-base">Google Ads</CardTitle>
                <CardDescription className="text-xs mt-0">Capture leads directly from Google Ads lead form extensions</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <IntegrationStatusBadge connected={isConnectedGg} />
              {expanded.gg ? <ArrowUp className="h-4 w-4 text-slate-400" /> : <ArrowDown className="h-4 w-4 text-slate-400" />}
            </div>
          </div>
        </CardHeader>
        {expanded.gg && <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Client ID</Label>
              <Input value={gg.clientId} onChange={e => setGg(p => ({ ...p, clientId: e.target.value }))} placeholder="xxxx.apps.googleusercontent.com" />
            </div>
            <div className="space-y-1.5">
              <Label>Client Secret</Label>
              <PwdInput id="gg_clientSecret" value={gg.clientSecret} onChange={v => setGg(p => ({ ...p, clientSecret: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>Developer Token</Label>
              <PwdInput id="gg_developerToken" value={gg.developerToken} onChange={v => setGg(p => ({ ...p, developerToken: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} />
            </div>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => saveSection('google', gg)} disabled={saving.google}>
              {saving.google && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Save
            </Button>
          </div>
        </CardContent>}
      </Card>

      {/* ── Card 3: WhatsApp Business API ── */}
      <Card>
        <CardHeader className="cursor-pointer select-none" onClick={() => toggle('wa')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#25D366' }}>
                <MessageSquare className="h-4 w-4 text-white" />
              </div>
              <div>
                <CardTitle className="text-base">WhatsApp Business</CardTitle>
                <CardDescription className="text-xs mt-0">Required for WhatsApp automation and auto-messaging features</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <IntegrationStatusBadge connected={isConnectedWa} />
              {expanded.wa ? <ArrowUp className="h-4 w-4 text-slate-400" /> : <ArrowDown className="h-4 w-4 text-slate-400" />}
            </div>
          </div>
        </CardHeader>
        {expanded.wa && <CardContent className="space-y-4">
          {(mode === 'tenant' || (mode === 'platform' && whatsappAccountsBase)) && (
            /* ── Tenant / vendor view + Platform with whatsappAccountsBase ── */
            <>
              {/* ── Legacy migration banner ──────────────────────────────
                  Show when old phoneNumberId + accessToken exist in settings
                  but no WhatsappAccount row has been created yet.
                  Hidden in platform mode — no legacy migration needed there. */}
              {mode === 'tenant' && waAccounts.length === 0 && wa.phoneNumberId && wa.accessToken && !waMigrationNeedsWaba && (
                <div className="flex items-center justify-between gap-3 px-4 py-3 bg-indigo-50 rounded-xl border border-indigo-100">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-indigo-800">Existing setup detected</p>
                    <p className="text-xs text-indigo-600 mt-0.5 truncate">
                      Phone Number ID: <code className="bg-white/70 px-1 rounded">{wa.phoneNumberId}</code> — click to restore
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="flex-shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white"
                    disabled={waMigrating}
                    onClick={async () => {
                      setWaMigrating(true);
                      try {
                        const r = await apiClient.post('/whatsapp/accounts/migrate-legacy', {});
                        if ((r.data as any)?.needsWabaId) {
                          setWaMigrationNeedsWaba({ phoneNumberId: (r.data as any).phoneNumberId });
                        } else {
                          const accounts = await apiClient.get('/whatsapp/accounts');
                          setWaAccounts(Array.isArray(accounts.data) ? accounts.data : []);
                        }
                      } catch (e: any) {
                        alert(e?.response?.data?.message || 'Migration failed. Please use the Access Token tab.');
                      } finally {
                        setWaMigrating(false);
                      }
                    }}
                  >
                    {waMigrating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Restore Connection'}
                  </Button>
                </div>
              )}

              {/* WABA ID fallback — only shown when auto-detect failed */}
              {waMigrationNeedsWaba && (
                <div className="space-y-3 px-4 py-3 bg-amber-50 rounded-xl border border-amber-100">
                  <div>
                    <p className="text-sm font-semibold text-amber-800">One more step — enter your WABA ID</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Your token type doesn't expose the WABA ID automatically. Find it in{' '}
                      <a href="https://business.facebook.com/wa/manage/home/" target="_blank" rel="noreferrer" className="underline">WhatsApp Manager</a>{' '}
                      → Account → About → WABA ID.
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Input
                      value={waMigrationWabaId}
                      onChange={e => setWaMigrationWabaId(e.target.value.replace(/\D/g, ''))}
                      placeholder="e.g. 102290129999"
                      className="w-48"
                    />
                    <Button
                      size="sm"
                      disabled={waMigrationWabaId.length < 5 || waMigrating}
                      onClick={async () => {
                        setWaMigrating(true);
                        try {
                          await apiClient.post('/whatsapp/accounts/migrate-legacy', { wabaId: waMigrationWabaId });
                          const accounts = await apiClient.get('/whatsapp/accounts');
                          setWaAccounts(Array.isArray(accounts.data) ? accounts.data : []);
                          setWaMigrationNeedsWaba(null);
                          setWaMigrationWabaId('');
                        } catch (e: any) {
                          alert(e?.response?.data?.message || 'Migration failed. Please use the Access Token tab.');
                        } finally {
                          setWaMigrating(false);
                        }
                      }}
                    >
                      {waMigrating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Connect'}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setWaMigrationNeedsWaba(null)}>Cancel</Button>
                  </div>
                </div>
              )}

              {/* ── Provider Cards (Bitrix/Zoho-style accordion) ── */}
              <div className="space-y-2.5">

                {/* ── Active connection switcher — only ONE runs at a time ── */}
                {waAccounts.length >= 2 && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50/70 p-3 mb-2">
                    <p className="text-xs font-semibold text-slate-700 mb-0.5">Active connection</p>
                    <p className="text-[11px] text-slate-500 mb-2.5">Only one WhatsApp connection runs at a time. Switching keeps the others’ credentials — no re-entry needed.</p>
                    <div className="space-y-1.5">
                      {waAccounts.map((a: any) => {
                        const prov = String(a.provider || 'META');
                        const provLabel = prov === 'WATI' ? 'Wati' : prov === 'GUPSHUP' ? 'Gupshup' : 'Meta';
                        const isActive = a.status === 'CONNECTED';
                        return (
                          <div key={a.id} className="flex items-center justify-between gap-3 rounded-lg bg-white border border-slate-100 px-3 py-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-slate-800 truncate">
                                {provLabel} <span className="text-slate-400 font-normal">· {a.displayPhoneNumber}</span>
                              </p>
                            </div>
                            {isActive ? (
                              <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-full px-2.5 py-1 flex-shrink-0">
                                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active
                              </span>
                            ) : (
                              <button
                                type="button"
                                onClick={() => activateWaAccount(a.id)}
                                disabled={waActivating}
                                className="text-[11px] font-semibold text-[#6366F1] border border-indigo-200 rounded-full px-3 py-1 hover:bg-indigo-50 disabled:opacity-50 flex-shrink-0"
                              >
                                {waActivating ? 'Switching…' : 'Make active'}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── Card 1: Meta WhatsApp (Recommended) ── */}
                <div className={`rounded-xl border-2 transition-colors ${waOpenCard === 'meta' ? 'border-emerald-200' : 'border-slate-100'}`}>
                  <button
                    className="w-full flex items-center justify-between px-4 py-3.5 text-left"
                    onClick={() => { setWaOpenCard(waOpenCard === 'meta' ? null : 'meta'); setWaStep('idle'); setWaPopupData(null); setWaPin(''); }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0" style={{ backgroundColor: '#25D366' }}>
                        <svg viewBox="0 0 24 24" className="h-5 w-5 fill-white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-semibold text-slate-800">Meta WhatsApp Business</p>
                          <span className="text-[10px] font-medium bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Recommended</span>
                        </div>
                        <p className="text-xs text-slate-500 mt-0.5">Official Meta integration · No technical setup needed · ~2 min</p>
                      </div>
                    </div>
                    {waOpenCard === 'meta'
                      ? <ArrowUp className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      : <ArrowDown className="h-4 w-4 text-slate-400 flex-shrink-0" />}
                  </button>

                  {waOpenCard === 'meta' && (
                    <div className="border-t border-emerald-100 px-4 pb-4 space-y-4 pt-4">

                      {/* ── Complete step-by-step setup guide ─────────────────
                          Embedded Signup is disabled until BSP approval, so the
                          permanent-token flow below is the real setup path. This
                          collapsible walks a vendor through it end-to-end. */}
                      <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 overflow-hidden">
                        <button
                          type="button"
                          className="w-full flex items-center justify-between px-3.5 py-3 text-left"
                          onClick={() => setWaShowGuide(v => !v)}
                        >
                          <span className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
                            <span className="text-base">📖</span>
                            Complete setup guide — connect your number in 8 steps
                          </span>
                          {waShowGuide ? <ArrowUp className="h-4 w-4 text-emerald-600 flex-shrink-0" /> : <ArrowDown className="h-4 w-4 text-emerald-600 flex-shrink-0" />}
                        </button>

                        {waShowGuide && (
                          <div className="px-3.5 pb-4 pt-1 text-xs text-slate-600 leading-relaxed space-y-3 border-t border-emerald-100">
                            <div className="pt-3">
                              <p className="font-semibold text-slate-700 mb-1">Before you start, you need:</p>
                              <ul className="list-disc pl-4 space-y-1">
                                <li>A <strong>Meta (Facebook) Business account</strong> — create one free at{' '}
                                  <a href="https://business.facebook.com/" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">business.facebook.com</a>.</li>
                                <li>A <strong>phone number that is NOT active on the normal WhatsApp / WhatsApp Business app</strong>. If it is, delete that WhatsApp account first, or use a fresh number.</li>
                                <li>Admin access to the business (to create tokens and assign assets).</li>
                              </ul>
                            </div>

                            <ol className="list-decimal pl-4 space-y-2.5 marker:font-semibold marker:text-emerald-700">
                              <li>
                                <strong>Create a Meta app.</strong> Go to{' '}
                                <a href="https://developers.facebook.com/apps/" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">developers.facebook.com → My Apps → Create App</a>,
                                choose type <strong>Business</strong>, then add the <strong>WhatsApp</strong> product to it.
                              </li>
                              <li>
                                <strong>Add your WhatsApp number.</strong> In the app under <em>WhatsApp → API Setup</em>, add and verify your business phone number by OTP (or start with the free Meta test number to try it out).
                              </li>
                              <li>
                                <strong>Create a permanent access token.</strong> Open{' '}
                                <a href="https://business.facebook.com/settings/system-users" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">Business Settings → Users → System Users</a>{' '}
                                → <em>Add</em> a system user (Admin) → <em>Generate new token</em> → select your app → set expiry to <strong>Never</strong> → tick{' '}
                                <code className="bg-white px-1 rounded text-[11px]">whatsapp_business_messaging</code> and{' '}
                                <code className="bg-white px-1 rounded text-[11px]">whatsapp_business_management</code> → Generate. <strong>Copy the token now</strong> — Meta shows it only once.
                              </li>
                              <li>
                                <strong>Give the system user access to your WhatsApp account.</strong> In{' '}
                                <a href="https://business.facebook.com/settings/whatsapp-business-accounts" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">Business Settings → Accounts → WhatsApp Accounts</a>,
                                select your WABA → <em>Assign people / system users</em> → add your system user with <strong>Full control</strong>.
                              </li>
                              <li>
                                <strong>Find your IDs.</strong> In{' '}
                                <a href="https://business.facebook.com/wa/manage/phone-numbers/" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">WhatsApp Manager</a>:
                                the <strong>Phone Number ID</strong> is on your number, and the <strong>WABA ID</strong> is under <em>Account → About</em>.
                              </li>
                              <li>
                                <strong>Paste them below.</strong> Enter the <strong>Phone Number ID</strong>, <strong>WABA ID</strong>, and <strong>Access Token</strong> in the fields under
                                “Advanced: connect with permanent access token” and click <strong>Save</strong>.
                              </li>
                              <li>
                                <strong>Connect the webhook (to receive replies).</strong> Copy the <strong>Callback URL</strong> and <strong>Verify Token</strong> shown in the blue box below into{' '}
                                <em>Meta App → WhatsApp → Configuration → Edit</em>, click <strong>Verify and save</strong>, then <strong>Subscribe</strong> to the{' '}
                                <code className="bg-white px-1 rounded text-[11px]">messages</code> field. This turns on incoming messages + delivery status.
                              </li>
                              <li>
                                <strong>Send a test.</strong> Open the WhatsApp inbox and send a message to confirm everything works. Done! 🎉
                              </li>
                            </ol>

                            <div className="rounded-md bg-amber-50 border border-amber-100 px-3 py-2 text-[11px] text-amber-800">
                              <strong>Note:</strong> to message customers who haven’t messaged you first, you’ll use approved <strong>message templates</strong> — create and submit them from the <em>Templates</em> tab after connecting. Meta usually reviews them within minutes to a few hours.
                            </div>
                            <p className="text-[11px] text-slate-500">
                              Stuck anywhere? Email <a href="mailto:support@brixlead.com" className="text-indigo-500 hover:underline">support@brixlead.com</a> and we’ll help you connect.
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Embedded Signup — idle */}
                      {/* NOTE: Button hidden — requires Meta BSP status (pending approval).
                          Re-enable by removing the {false &&} wrapper when BSP approved. */}
                      {waStep === 'idle' && (
                        <div className="flex flex-col items-center gap-4 py-2">
                          {false && <div className="text-center max-w-xs">
                            <p className="text-sm font-semibold text-slate-800">Connect your WhatsApp Business number</p>
                            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                              Click the button below. A Meta popup will guide you through authorizing your WhatsApp Business Account in ~2 minutes.
                            </p>
                          </div>}
                          {false && <Button
                            disabled={!waEmbeddedConfig.available || waConnecting}
                            onClick={() => {
                              if (!(window as any).FB) {
                                const script = document.createElement('script');
                                script.src = 'https://connect.facebook.net/en_US/sdk.js';
                                script.onload = () => {
                                  (window as any).FB.init({ appId: waEmbeddedConfig.appId, version: 'v19.0', cookie: true, xfbml: false });
                                  launchWaPopup();
                                };
                                document.body.appendChild(script);
                              } else {
                                launchWaPopup();
                              }
                              function launchWaPopup() {
                                const onMsg = (e: MessageEvent) => {
                                  if (e.origin !== 'https://www.facebook.com' && e.origin !== 'https://web.facebook.com') return;
                                  try {
                                    const d = typeof e.data === 'string' ? JSON.parse(e.data) : e.data;
                                    if (d?.type === 'WA_EMBEDDED_SIGNUP' && d?.event === 'FINISH') {
                                      setWaPopupData(prev => ({ ...prev ?? { code: '' }, phoneNumberId: d.data?.phone_number_id ?? '', wabaId: d.data?.waba_id ?? '' }));
                                    }
                                  } catch { /* ignore */ }
                                };
                                window.addEventListener('message', onMsg);
                                const loginOpts: Record<string, any> = {
                                  response_type: 'code',
                                  override_default_response_type: true,
                                  extras: { feature: 'whatsapp_embedded_signup', sessionInfoVersion: 2 },
                                };
                                if (waEmbeddedConfig.configId) {
                                  loginOpts.config_id = waEmbeddedConfig.configId;
                                } else {
                                  loginOpts.scope = 'whatsapp_business_management,business_management';
                                }
                                (window as any).FB.login((res: any) => {
                                  window.removeEventListener('message', onMsg);
                                  if (res?.authResponse?.code) {
                                    setWaPopupData(prev => ({ phoneNumberId: prev?.phoneNumberId ?? '', wabaId: prev?.wabaId ?? '', code: res.authResponse.code }));
                                    setWaStep('pin');
                                  }
                                }, loginOpts);
                              }
                            }}
                            className="gap-2 px-6 h-11 text-sm font-medium"
                            style={{ backgroundColor: '#25D366' }}
                          >
                            {waConnecting ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                              <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                            )}
                            Connect WhatsApp Business
                          </Button>}
                          {false && !waEmbeddedConfig.available && (
                            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 max-w-xs text-center">
                              Platform admin has not configured WhatsApp credentials yet. Use the manual token option below or contact support.
                            </p>
                          )}

                          {/* Advanced: manual token form toggle */}
                          <div className="w-full pt-3">
                            <button
                              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                              onClick={() => setWaMetaShowToken(v => !v)}
                            >
                              {waMetaShowToken ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                              Advanced: connect with permanent access token instead
                            </button>
                            {waMetaShowToken && (
                              <div className="mt-4 space-y-4">
                                <div className="px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-100">
                                  <p className="text-xs text-slate-600 leading-relaxed">
                                    Generate a <strong>permanent System User access token</strong> from{' '}
                                    <a href="https://business.facebook.com/settings/system-users" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">Meta Business Suite → System Users</a>.
                                    Assign <code className="bg-white px-1 rounded text-[11px]">whatsapp_business_messaging</code> permission. The Phone Number ID and WABA ID are found in{' '}
                                    <a href="https://business.facebook.com/wa/manage/phone-numbers/" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">WhatsApp Manager</a>.
                                  </p>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="space-y-1.5">
                                    <Label>Phone Number ID</Label>
                                    <Input autoComplete="off" value={waTokenForm.phoneNumberId} onChange={e => setWaTokenForm(p => ({ ...p, phoneNumberId: e.target.value }))} placeholder="e.g. 109085652078163" />
                                  </div>
                                  <div className="space-y-1.5">
                                    <Label>WABA ID</Label>
                                    <Input autoComplete="off" value={waTokenForm.wabaId} onChange={e => setWaTokenForm(p => ({ ...p, wabaId: e.target.value }))} placeholder="e.g. 102290129999" />
                                  </div>
                                  <div className="space-y-1.5 col-span-2">
                                    <Label>Permanent Access Token</Label>
                                    <PwdInput
                                      id="waToken_accessToken"
                                      value={waTokenForm.accessToken}
                                      onChange={v => setWaTokenForm(p => ({ ...p, accessToken: v }))}
                                      showMap={showPwd}
                                      onToggle={togglePwd}
                                      onReveal={revealWaToken}
                                      inputRef={waTokenInputRef}
                                      placeholder={
                                        waAccounts.some(a => a.phoneNumberId === waTokenForm.phoneNumberId && a.status === 'CONNECTED') && !waTokenForm.accessToken
                                          ? '••••••••••••••••••••••• (saved)'
                                          : 'EAAxxxxxxx…'
                                      }
                                    />
                                    {waAccounts.some(a => a.phoneNumberId === waTokenForm.phoneNumberId && a.status === 'CONNECTED') && !waTokenForm.accessToken && (
                                      <p className="text-xs text-emerald-600 flex items-center gap-1 mt-1">
                                        <span>✓</span>
                                        <span>Token saved securely — paste a new one only if you want to update it.</span>
                                      </p>
                                    )}
                                  </div>
                                </div>
                                {/* Webhook URL + Verify Token */}
                                <div className="px-3 py-3 bg-blue-50 rounded-lg border border-blue-100 space-y-2.5">
                                  <p className="text-xs font-semibold text-blue-800">Set these in your Meta App → WhatsApp → Configuration:</p>
                                  {/* Callback URL */}
                                  <div className="flex items-center gap-2">
                                    <div className="flex-1 min-w-0">
                                      <p className="text-[10px] text-blue-600 font-medium mb-0.5">Callback URL</p>
                                      <code className="text-xs text-blue-700 truncate block">{waWebhookConfig.webhookUrl}</code>
                                    </div>
                                    <button type="button"
                                      onClick={() => navigator.clipboard.writeText(waWebhookConfig.webhookUrl).then(() => toast('success', 'Copied!'))}
                                      className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex-shrink-0 border border-blue-200 rounded px-2 py-1 bg-white">
                                      Copy
                                    </button>
                                  </div>
                                  {/* Verify Token */}
                                  <div className="space-y-1.5">
                                    <p className="text-[10px] text-blue-600 font-medium">Verify Token</p>
                                    {mode === 'platform' ? (
                                      /* Super-admin: Generate + Save inline */
                                      <div className="space-y-2">
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="text"
                                            value={metaApp.webhookVerifyToken}
                                            onChange={e => setMetaApp(p => ({ ...p, webhookVerifyToken: e.target.value, verifyTokenTouched: true }))}
                                            placeholder="Click Generate to create a token"
                                            autoComplete="off"
                                            className="flex-1 text-xs bg-white border border-blue-200 rounded px-2 py-1.5 text-blue-700 outline-none focus:ring-1 focus:ring-blue-400"
                                          />
                                          <button type="button"
                                            onClick={() => {
                                              const token = Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
                                              setMetaApp(p => ({ ...p, webhookVerifyToken: token, verifyTokenTouched: true }));
                                            }}
                                            className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex-shrink-0 border border-blue-200 rounded px-2 py-1.5 bg-white">
                                            Generate
                                          </button>
                                          {metaApp.webhookVerifyToken && (
                                            <button type="button"
                                              onClick={() => navigator.clipboard.writeText(metaApp.webhookVerifyToken).then(() => toast('success', 'Copied!'))}
                                              className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex-shrink-0 border border-blue-200 rounded px-2 py-1.5 bg-white">
                                              Copy
                                            </button>
                                          )}
                                        </div>
                                        {metaApp.verifyTokenTouched && (
                                          <div className="flex justify-end">
                                            <button type="button"
                                              onClick={async () => { await saveMetaApp(); toast('success', 'Verify token saved'); }}
                                              disabled={savingMetaApp}
                                              className="text-xs bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded px-3 py-1.5 disabled:opacity-50">
                                              {savingMetaApp ? 'Saving…' : 'Save'}
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                    ) : waWebhookConfig.verifyTokenSet && waWebhookConfig.verifyToken ? (
                                      /* Vendor: show token with Copy */
                                      <div className="flex items-center gap-2">
                                        <code className="text-xs text-blue-700 flex-1 truncate">{waWebhookConfig.verifyToken}</code>
                                        <button type="button"
                                          onClick={() => navigator.clipboard.writeText(waWebhookConfig.verifyToken!).then(() => toast('success', 'Copied!'))}
                                          className="text-xs text-blue-600 hover:text-blue-800 font-semibold flex-shrink-0 border border-blue-200 rounded px-2 py-1 bg-white">
                                          Copy
                                        </button>
                                      </div>
                                    ) : (
                                      <span className="text-xs text-amber-600">Not set — contact platform admin</span>
                                    )}
                                  </div>
                                </div>
                                <div className="flex justify-end">
                                  <Button
                                    disabled={!waTokenForm.phoneNumberId || !waTokenForm.wabaId || !waTokenForm.accessToken || waConnecting}
                                    onClick={async () => {
                                      setWaConnecting(true);
                                      try {
                                        await apiClient.post(`${waApiBase}/accounts/connect-token`, waTokenForm);
                                        const r = await apiClient.get(`${waApiBase}/accounts`);
                                        setWaAccounts(Array.isArray(r.data) ? r.data : []);
                                        setWaTokenForm(p => ({ ...p, accessToken: '' }));
                                      } catch (e: any) {
                                        alert(e?.response?.data?.message || 'Connection failed. Check your credentials and try again.');
                                      } finally {
                                        setWaConnecting(false);
                                      }
                                    }}
                                  >
                                    {waConnecting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Connecting…</> : 'Connect'}
                                  </Button>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Embedded Signup — PIN step */}
                      {waStep === 'pin' && (
                        <div className="space-y-4 py-2">
                          <div className="px-4 py-3 bg-blue-50 rounded-xl border border-blue-100">
                            <p className="text-sm font-medium text-blue-800">Almost done! Enter your 2-step verification PIN</p>
                            <p className="text-xs text-blue-600 mt-1">This is the 6-digit PIN you set during the WhatsApp Business setup popup. It secures your number.</p>
                          </div>
                          {waPopupData?.phoneNumberId && (
                            <p className="text-xs text-slate-500">Phone Number ID: <code className="bg-slate-100 px-1 rounded">{waPopupData.phoneNumberId}</code></p>
                          )}
                          <div className="space-y-1.5">
                            <Label>6-digit PIN</Label>
                            <Input
                              value={waPin}
                              onChange={e => setWaPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                              placeholder="••••••"
                              maxLength={6}
                              className="text-center text-2xl tracking-widest font-mono w-40"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button variant="outline" onClick={() => { setWaStep('idle'); setWaPopupData(null); setWaPin(''); }}>Back</Button>
                            <Button
                              disabled={waPin.length !== 6 || waConnecting || !waPopupData?.code || !waPopupData?.phoneNumberId || !waPopupData?.wabaId}
                              onClick={async () => {
                                if (!waPopupData) return;
                                setWaConnecting(true);
                                try {
                                  await apiClient.post(`${waApiBase}/onboarding/complete`, {
                                    code: waPopupData.code,
                                    phoneNumberId: waPopupData.phoneNumberId,
                                    wabaId: waPopupData.wabaId,
                                    pin: waPin,
                                  });
                                  const r = await apiClient.get(`${waApiBase}/accounts`);
                                  setWaAccounts(Array.isArray(r.data) ? r.data : []);
                                  setWaStep('idle');
                                  setWaPopupData(null);
                                  setWaPin('');
                                } catch (e: any) {
                                  alert(e?.response?.data?.message || 'Connection failed. Please try again.');
                                } finally {
                                  setWaConnecting(false);
                                }
                              }}
                            >
                              {waConnecting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Connecting…</> : 'Complete Setup'}
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* ── Card 2: Wati ── */}
                <div className={`rounded-xl border-2 transition-colors ${waOpenCard === 'wati' ? 'border-green-200' : 'border-slate-100'}`}>
                  <button
                    className="w-full flex items-center justify-between px-4 py-3.5 text-left"
                    onClick={() => setWaOpenCard(waOpenCard === 'wati' ? null : 'wati')}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-green-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xl">🟢</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Wati</p>
                        <p className="text-xs text-slate-500 mt-0.5">Connect via your Wati BSP account</p>
                      </div>
                    </div>
                    {waOpenCard === 'wati'
                      ? <ArrowUp className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      : <ArrowDown className="h-4 w-4 text-slate-400 flex-shrink-0" />}
                  </button>
                  {waOpenCard === 'wati' && (
                    <div className="border-t border-green-100 px-4 pb-4 pt-4 space-y-4">
                      <div className="px-3 py-2.5 bg-green-50 rounded-lg border border-green-100">
                        <p className="text-xs text-green-800 leading-relaxed font-semibold mb-1.5">Connect Wati in 5 steps</p>
                        <ol className="text-xs text-green-700 space-y-1 list-decimal list-inside">
                          <li>Login to <a href="https://app.wati.io" target="_blank" rel="noreferrer" className="underline">app.wati.io</a> (you should already have a Wati plan with your WhatsApp number approved).</li>
                          <li>Open <strong>Settings → API Docs / Access Token</strong>.</li>
                          <li>Copy your <strong>API Endpoint</strong> — it looks like <code className="bg-white px-1 rounded">https://live-mt-server.wati.io/123456</code>.</li>
                          <li>Click <strong>Generate Access Token</strong> (choose a long / no-expiry token) and copy it.</li>
                          <li>Paste both below and click <strong>Connect via Wati</strong>. That’s it — no Meta app needed.</li>
                        </ol>
                        <p className="text-[11px] text-green-700 mt-2 pt-2 border-t border-green-100">
                          To message customers outside the 24-hour window, keep your <strong>templates approved inside Wati</strong> — use the same template name here.
                        </p>
                      </div>
                      <div className="px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
                        <p className="text-[11px] text-amber-800 leading-relaxed">
                          <strong>Good to know:</strong> Wati handles <strong>sending</strong> (workflows, reminders, one-to-one, and template <strong>broadcasts</strong>) and <strong>receiving</strong> — incoming replies land in your inbox once you set the webhook URL shown after connecting.
                        </p>
                      </div>
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <Label>API Endpoint URL</Label>
                          <Input autoComplete="off" value={waWatiForm.apiEndpoint} onChange={e => setWaWatiForm(p => ({ ...p, apiEndpoint: e.target.value }))} placeholder="https://live-mt-server.wati.io/123456" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>Access Token (API Key)</Label>
                          <PwdInput id="wati_apiKey" value={waWatiForm.apiKey} onChange={v => setWaWatiForm(p => ({ ...p, apiKey: v }))} showMap={showPwd} onToggle={togglePwd} placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9…" />
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Button
                          disabled={!waWatiForm.apiEndpoint || !waWatiForm.apiKey || waConnecting}
                          onClick={async () => {
                            setWaConnecting(true);
                            try {
                              await apiClient.post(`${waApiBase}/accounts/connect-wati`, waWatiForm);
                              const r = await apiClient.get(`${waApiBase}/accounts`);
                              setWaAccounts(Array.isArray(r.data) ? r.data : []);
                              setWaWatiForm(p => ({ ...p, apiKey: '' }));
                            } catch (e: any) {
                              alert(e?.response?.data?.message || 'Connection failed. Check your API Key and Endpoint URL.');
                            } finally { setWaConnecting(false); }
                          }}
                        >
                          {waConnecting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Connecting…</> : 'Connect via Wati'}
                        </Button>
                      </div>

                      {/* Connected → show the inbound webhook URL to enable replies. */}
                      {(() => {
                        const acct = waAccounts.find((a: any) => a.provider === 'WATI' && a.status === 'CONNECTED' && a.inboundWebhookUrl);
                        if (!acct) return null;
                        return (
                          <div className="px-3 py-3 bg-green-50 rounded-lg border border-green-100 space-y-2">
                            <p className="text-xs font-semibold text-green-800">✓ Connected — now turn on incoming replies</p>
                            <p className="text-[11px] text-green-700 leading-relaxed">
                              Paste this URL into <strong>Wati → Settings → Webhooks</strong> and enable the <strong>“Message Received”</strong> event, so customer replies land in your inbox:
                            </p>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 min-w-0 text-[11px] text-green-800 bg-white rounded px-2 py-1.5 truncate border border-green-100">{acct.inboundWebhookUrl}</code>
                              <button type="button"
                                onClick={() => navigator.clipboard.writeText(acct.inboundWebhookUrl).then(() => toast('success', 'Copied!'))}
                                className="text-xs text-green-700 hover:text-green-900 font-semibold flex-shrink-0 border border-green-200 rounded px-2 py-1.5 bg-white">
                                Copy
                              </button>
                            </div>
                            <div className="text-[11px] text-green-700 leading-relaxed bg-white/60 rounded px-2.5 py-2 border border-green-100">
                              <p className="font-semibold text-green-800 mb-0.5">Receiving photos, files &amp; button taps</p>
                              <p>
                                The <strong>“Message Received”</strong> event already covers <strong>all</strong> reply types — text, images, videos, documents, and taps on your buttons / list options. No extra setting needed. Just make sure the webhook is <strong>Enabled</strong> and not filtered to text-only. Images &amp; files open right inside the chat; a button tap shows as the option the customer chose.
                              </p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>

                {/* ── Card 3: Gupshup ── */}
                <div className={`rounded-xl border-2 transition-colors ${waOpenCard === 'gupshup' ? 'border-blue-200' : 'border-slate-100'}`}>
                  <button
                    className="w-full flex items-center justify-between px-4 py-3.5 text-left"
                    onClick={() => setWaOpenCard(waOpenCard === 'gupshup' ? null : 'gupshup')}
                  >
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-xl bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <span className="text-xl">🔵</span>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Gupshup</p>
                        <p className="text-xs text-slate-500 mt-0.5">Connect via your Gupshup account</p>
                      </div>
                    </div>
                    {waOpenCard === 'gupshup'
                      ? <ArrowUp className="h-4 w-4 text-slate-400 flex-shrink-0" />
                      : <ArrowDown className="h-4 w-4 text-slate-400 flex-shrink-0" />}
                  </button>
                  {waOpenCard === 'gupshup' && (
                    <div className="border-t border-blue-100 px-4 pb-4 pt-4 space-y-4">
                      <div className="px-3 py-2.5 bg-blue-50 rounded-lg border border-blue-100">
                        <p className="text-xs text-blue-800 leading-relaxed font-semibold mb-1.5">Connect Gupshup in 5 steps</p>
                        <ol className="text-xs text-blue-700 space-y-1 list-decimal list-inside">
                          <li>Login to <a href="https://www.gupshup.io" target="_blank" rel="noreferrer" className="underline">gupshup.io</a> (you should already have a live WhatsApp app with your number approved).</li>
                          <li>Open your <strong>WhatsApp app dashboard</strong> and note the <strong>App Name</strong> exactly as shown.</li>
                          <li>Go to <strong>Dashboard → Settings → API Key</strong> (or the Partner Portal → API Keys).</li>
                          <li>Copy your <strong>API Key</strong>.</li>
                          <li>Paste the API Key + App Name below and click <strong>Connect via Gupshup</strong>.</li>
                        </ol>
                      </div>
                      <div className="px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
                        <p className="text-[11px] text-amber-800 leading-relaxed">
                          <strong>Good to know:</strong> Gupshup handles <strong>sending</strong> (session text, approved templates, and template <strong>broadcasts</strong>) and <strong>receiving</strong> — incoming replies land in your inbox once you set the callback URL shown after connecting. Templates must be created &amp; approved in your Gupshup dashboard (same name).
                        </p>
                      </div>
                      <div className="space-y-3">
                        <div className="space-y-1.5">
                          <Label>API Key</Label>
                          <PwdInput id="gupshup_apiKey" value={waGupshupForm.apiKey} onChange={v => setWaGupshupForm(p => ({ ...p, apiKey: v }))} showMap={showPwd} onToggle={togglePwd} placeholder="Your Gupshup API key" />
                        </div>
                        <div className="space-y-1.5">
                          <Label>App Name</Label>
                          <Input autoComplete="off" value={waGupshupForm.appName} onChange={e => setWaGupshupForm(p => ({ ...p, appName: e.target.value }))} placeholder="MyBusinessApp" />
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Button
                          disabled={!waGupshupForm.apiKey || !waGupshupForm.appName || waConnecting}
                          onClick={async () => {
                            setWaConnecting(true);
                            try {
                              await apiClient.post(`${waApiBase}/accounts/connect-gupshup`, waGupshupForm);
                              const r = await apiClient.get(`${waApiBase}/accounts`);
                              setWaAccounts(Array.isArray(r.data) ? r.data : []);
                              setWaGupshupForm(p => ({ ...p, apiKey: '' }));
                            } catch (e: any) {
                              alert(e?.response?.data?.message || 'Connection failed. Check your API Key and App Name.');
                            } finally { setWaConnecting(false); }
                          }}
                        >
                          {waConnecting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" />Connecting…</> : 'Connect via Gupshup'}
                        </Button>
                      </div>

                      {/* Connected → show the inbound webhook URL to enable replies. */}
                      {(() => {
                        const acct = waAccounts.find((a: any) => a.provider === 'GUPSHUP' && a.status === 'CONNECTED' && a.inboundWebhookUrl);
                        if (!acct) return null;
                        return (
                          <div className="px-3 py-3 bg-blue-50 rounded-lg border border-blue-100 space-y-2">
                            <p className="text-xs font-semibold text-blue-800">✓ Connected — now turn on incoming replies</p>
                            <p className="text-[11px] text-blue-700 leading-relaxed">
                              Set this <strong>Callback URL</strong> in your <strong>Gupshup app → Settings → Callback URL</strong> (and enable it), so customer replies land in your inbox:
                            </p>
                            <div className="flex items-center gap-2">
                              <code className="flex-1 min-w-0 text-[11px] text-blue-800 bg-white rounded px-2 py-1.5 truncate border border-blue-100">{acct.inboundWebhookUrl}</code>
                              <button type="button"
                                onClick={() => navigator.clipboard.writeText(acct.inboundWebhookUrl).then(() => toast('success', 'Copied!'))}
                                className="text-xs text-blue-700 hover:text-blue-900 font-semibold flex-shrink-0 border border-blue-200 rounded px-2 py-1.5 bg-white">
                                Copy
                              </button>
                            </div>
                            <div className="text-[11px] text-blue-700 leading-relaxed bg-white/60 rounded px-2.5 py-2 border border-blue-100">
                              <p className="font-semibold text-blue-800 mb-0.5">Receiving photos, files &amp; button taps</p>
                              <p>
                                In your Gupshup app’s callback settings, turn on <strong>Inbound Messages</strong> (and keep <strong>Inbound Media</strong> / message events enabled) — that one callback delivers <strong>all</strong> reply types: text, images, videos, documents, and taps on your quick-reply / list options. Images &amp; files open right inside the chat; a button tap shows as the option the customer chose.
                              </p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Advanced: Meta App configuration — platform/super-admin only ── */}
              {mode === 'platform' && <div className="border-t border-slate-100 pt-3 mt-2">
                <button
                  className="flex items-center gap-2 text-xs text-slate-400 hover:text-slate-600 transition-colors"
                  onClick={() => setWaAdvancedOpen(v => !v)}
                >
                  {waAdvancedOpen ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />}
                  ⚙️ Advanced: configure your own Meta App
                </button>
                {waAdvancedOpen && (
                  <div className="mt-4 space-y-4">
                    <div className="px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-100">
                      <p className="text-xs text-slate-600 leading-relaxed">
                        Register your own Meta App at{' '}
                        <a href="https://developers.facebook.com/apps" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">developers.facebook.com</a>.
                        Add the <strong>WhatsApp Business</strong> product. Then save your credentials below and use the Meta connect button above.
                      </p>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="col-span-2 flex items-center justify-between">
                        <p className="text-xs font-medium text-slate-600">Your Meta App credentials</p>
                        {metaApp.source && (
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${
                            metaApp.source === 'tenant' ? 'bg-emerald-50 text-emerald-700' :
                            metaApp.source === 'platform' ? 'bg-amber-50 text-amber-700' :
                            'bg-slate-100 text-slate-500'
                          }`}>
                            {metaApp.source === 'tenant' ? 'Your custom app' : metaApp.source === 'platform' ? 'Platform default (shared)' : 'Env vars'}
                          </span>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        <Label>Meta App ID</Label>
                        <Input value={metaApp.metaAppId} onChange={e => setMetaApp(p => ({ ...p, metaAppId: e.target.value }))} placeholder="e.g. 1234567890" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Meta App Secret</Label>
                        <div className="flex gap-2">
                          <PwdInput id="metaApp_appSecret_adv" value={metaApp.metaAppSecretSet && !metaApp.secretTouched ? '************' : metaApp.metaAppSecret} onChange={v => setMetaApp(p => ({ ...p, metaAppSecret: v, secretTouched: true }))} placeholder={metaApp.metaAppSecretSet ? '(already set)' : 'Enter app secret'} showMap={showPwd} onToggle={togglePwd} />
                          {metaApp.metaAppSecretSet && !metaApp.secretTouched && (
                            <Button variant="outline" size="sm" className="border-red-100 text-red-600 hover:bg-red-50 h-9 px-3 text-xs flex-shrink-0" onClick={() => setMetaApp(p => ({ ...p, metaAppSecret: '', secretTouched: true, metaAppSecretSet: false }))}>Clear</Button>
                          )}
                        </div>
                      </div>
                      <div className="space-y-1.5 col-span-2">
                        <Label>Config ID <span className="text-slate-400 font-normal">(from Meta Embedded Signup config)</span></Label>
                        <Input value={metaApp.metaConfigId} onChange={e => setMetaApp(p => ({ ...p, metaConfigId: e.target.value }))} placeholder="e.g. 123456789" />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button onClick={async () => { await saveMetaApp(); const r = await apiClient.get(`${waApiBase}/onboarding/config`); setWaEmbeddedConfig(r.data); }} disabled={savingMetaApp}>
                        {savingMetaApp && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                        Save & Apply
                      </Button>
                    </div>
                  </div>
                )}
              </div>}
            </>
          )}
        </CardContent>}
      </Card>


      {/* ── AI moved to the AI Assistant section ── */}
      {mode !== 'platform' && (
        <Card>
          <CardContent className="py-4">
            <button
              type="button"
              onClick={() => navigate('/ai')}
              className="w-full flex items-center gap-3 text-left group"
            >
              <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Sparkles className="h-4 w-4 text-indigo-600" />
              </div>
              <div className="flex-1">
                <div className="text-sm font-semibold text-slate-800">AI Assistant &amp; Voice Calls</div>
                <div className="text-xs text-slate-400">Keys, toggles, agents and the call log now live in the AI Assistant section.</div>
              </div>
              <span className="text-sm font-medium text-[#6366F1] group-hover:underline whitespace-nowrap">Open →</span>
            </button>
          </CardContent>
        </Card>
      )}

      {/* ── AI Voice Calls — platform default (super-admin only) ── */}
      {mode === 'platform' && (
        <Card>
          <CardHeader className="cursor-pointer select-none" onClick={() => toggle('voice')}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                  <Sparkles className="h-4 w-4 text-indigo-600" />
                </div>
                <div>
                  <CardTitle className="text-base">AI Voice Calls (platform default)</CardTitle>
                  <CardDescription className="text-xs mt-0">Fallback voice provider for tenants who haven't set their own — Retell or ElevenLabs.</CardDescription>
                </div>
              </div>
              {expanded.voice ? <ArrowUp className="h-4 w-4 text-slate-400" /> : <ArrowDown className="h-4 w-4 text-slate-400" />}
            </div>
          </CardHeader>
          {expanded.voice && <CardContent className="space-y-4">
            <div className="space-y-1.5">
              <Label>Provider</Label>
              <SelectField className={SELECT_CLASSNAME} value={voice.provider} onChange={e => setVoice(p => ({ ...p, provider: e.target.value }))}>
                <option value="retell">Retell</option>
                <option value="elevenlabs">ElevenLabs</option>
              </SelectField>
            </div>
            <div className="space-y-1.5">
              <Label>{voice.provider === 'elevenlabs' ? 'ElevenLabs' : 'Retell'} API Key</Label>
              <PwdInput id="voice_apiKey" value={voice.apiKey} onChange={v => setVoice(p => ({ ...p, apiKey: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} placeholder={voice.provider === 'elevenlabs' ? 'sk_xxxxxxxx' : 'key_xxxxxxxx'} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              {voice.provider === 'elevenlabs' ? (
                <div className="space-y-1.5">
                  <Label>ElevenLabs Phone Number ID</Label>
                  <Input value={voice.elevenLabsPhoneNumberId} onChange={e => setVoice(p => ({ ...p, elevenLabsPhoneNumberId: e.target.value }))} placeholder="phnum_… (from ElevenLabs)" />
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label>From Number (caller ID)</Label>
                  <Input value={voice.fromNumber} onChange={e => setVoice(p => ({ ...p, fromNumber: e.target.value }))} placeholder="+14155551234" />
                </div>
              )}
              <div className="space-y-1.5">
                <Label>Default Agent ID</Label>
                <Input value={voice.defaultAgentId} onChange={e => setVoice(p => ({ ...p, defaultAgentId: e.target.value }))} placeholder="agent_… (fallback)" />
              </div>
            </div>
            <p className="text-xs text-slate-400">Tenants who fill their own AI Voice settings override this. The per-tenant master enable switch is unaffected — this only supplies default keys/agent.</p>
            <div className="flex justify-end">
              <Button onClick={() => saveSection('voice', voice)} disabled={saving.voice}>
                {saving.voice && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                Save
              </Button>
            </div>
          </CardContent>}
        </Card>
      )}

      {/* ── Card 4: Email Provider ── */}
      <Card>
        <CardHeader className="cursor-pointer select-none" onClick={() => toggle('email')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <Mail className="h-4 w-4 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-base">Email Provider</CardTitle>
                <CardDescription className="text-xs mt-0">Configure transactional email delivery for notifications and campaigns</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <IntegrationStatusBadge connected={isConnectedEmail} />
              {expanded.email ? <ArrowUp className="h-4 w-4 text-slate-400" /> : <ArrowDown className="h-4 w-4 text-slate-400" />}
            </div>
          </div>
        </CardHeader>
        {expanded.email && <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Provider</Label>
            <SelectField className={SELECT_CLASSNAME} value={email.provider} onChange={e => setEmail(p => ({ ...p, provider: e.target.value }))}>
              <option value="brevo">Brevo (recommended)</option>
              <option value="sendgrid">SendGrid</option>
              <option value="mailgun">Mailgun</option>
              <option value="resend">Resend</option>
              <option value="smtp">SMTP</option>
            </SelectField>
          </div>

          {/* Brevo */}
          {email.provider === 'brevo' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <Label>API Key</Label>
                <PwdInput id="email_apiKey" value={email.apiKey} onChange={v => setEmail(p => ({ ...p, apiKey: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} placeholder="xkeysib-xxxxxxxx" />
              </div>
              <div className="space-y-1.5">
                <Label>From Name</Label>
                <Input value={email.fromName} onChange={e => setEmail(p => ({ ...p, fromName: e.target.value }))} placeholder="Your Company" />
              </div>
              <div className="space-y-1.5">
                <Label>From Email</Label>
                <Input type="email" value={email.fromEmail} onChange={e => setEmail(p => ({ ...p, fromEmail: e.target.value }))} placeholder="noreply@company.com" />
              </div>
              <p className="col-span-2 text-xs text-slate-500">
                Get your API key from{' '}
                <a href="https://app.brevo.com/settings/keys/api" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">
                  Brevo Dashboard → SMTP & API → API Keys
                </a>
                . Free tier: 300 emails/day.
              </p>
              <div className="col-span-2 flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5">
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
                <p className="text-xs text-amber-800">
                  <span className="font-semibold">Important:</span> In your Brevo account, keep{' '}
                  <span className="font-semibold">&ldquo;Authorized IPs&rdquo; turned OFF</span>{' '}
                  (Brevo → Security → Authorized IPs). Emails are sent from our cloud servers, whose
                  IP address changes automatically — IP restrictions will block your emails. Your API
                  key stays secret, so it&rsquo;s safe to leave this off.
                </p>
              </div>
            </div>
          )}

          {/* SendGrid */}
          {email.provider === 'sendgrid' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <Label>API Key</Label>
                <PwdInput id="email_apiKey" value={email.apiKey} onChange={v => setEmail(p => ({ ...p, apiKey: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} placeholder="SG.xxxxxxxx" />
              </div>
              <div className="space-y-1.5">
                <Label>From Name</Label>
                <Input value={email.fromName} onChange={e => setEmail(p => ({ ...p, fromName: e.target.value }))} placeholder="Your Company" />
              </div>
              <div className="space-y-1.5">
                <Label>From Email</Label>
                <Input type="email" value={email.fromEmail} onChange={e => setEmail(p => ({ ...p, fromEmail: e.target.value }))} placeholder="noreply@company.com" />
              </div>
            </div>
          )}

          {/* Resend */}
          {email.provider === 'resend' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <Label>API Key</Label>
                <PwdInput id="email_apiKey" value={email.apiKey} onChange={v => setEmail(p => ({ ...p, apiKey: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} placeholder="re_xxxxxxxx" />
              </div>
              <div className="space-y-1.5">
                <Label>From Name</Label>
                <Input value={email.fromName} onChange={e => setEmail(p => ({ ...p, fromName: e.target.value }))} placeholder="Your Company" />
              </div>
              <div className="space-y-1.5">
                <Label>From Email</Label>
                <Input type="email" value={email.fromEmail} onChange={e => setEmail(p => ({ ...p, fromEmail: e.target.value }))} placeholder="noreply@company.com" />
              </div>
            </div>
          )}

          {/* Mailgun */}
          {email.provider === 'mailgun' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <Label>API Key</Label>
                <PwdInput id="email_apiKey" value={email.apiKey} onChange={v => setEmail(p => ({ ...p, apiKey: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} placeholder="key-xxxxxxxx" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>Domain</Label>
                <Input value={email.domain} onChange={e => setEmail(p => ({ ...p, domain: e.target.value }))} placeholder="mg.yourdomain.com" />
              </div>
              <div className="space-y-1.5">
                <Label>From Name</Label>
                <Input value={email.fromName} onChange={e => setEmail(p => ({ ...p, fromName: e.target.value }))} placeholder="Your Company" />
              </div>
              <div className="space-y-1.5">
                <Label>From Email</Label>
                <Input type="email" value={email.fromEmail} onChange={e => setEmail(p => ({ ...p, fromEmail: e.target.value }))} placeholder="noreply@company.com" />
              </div>
            </div>
          )}

          {/* SMTP */}
          {email.provider === 'smtp' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Host</Label>
                <Input value={email.host} onChange={e => setEmail(p => ({ ...p, host: e.target.value }))} placeholder="smtp.gmail.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Port</Label>
                <Input value={email.port} onChange={e => setEmail(p => ({ ...p, port: e.target.value }))} placeholder="587" />
              </div>
              <div className="space-y-1.5">
                <Label>Username</Label>
                <Input value={email.username} onChange={e => setEmail(p => ({ ...p, username: e.target.value }))} placeholder="your@email.com" />
              </div>
              <div className="space-y-1.5">
                <Label>Password</Label>
                <PwdInput id="email_password" value={email.password} onChange={v => setEmail(p => ({ ...p, password: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} />
              </div>
              <div className="space-y-1.5">
                <Label>From Name</Label>
                <Input value={email.fromName} onChange={e => setEmail(p => ({ ...p, fromName: e.target.value }))} placeholder="Your Company" />
              </div>
              <div className="space-y-1.5">
                <Label>From Email</Label>
                <Input type="email" value={email.fromEmail} onChange={e => setEmail(p => ({ ...p, fromEmail: e.target.value }))} placeholder="noreply@company.com" />
              </div>
            </div>
          )}

          <div className="flex items-center justify-between">
            {/* Test endpoint is tenant-scoped (uses tenant identity for the
                from-address validation). Hide the button on platform mode
                rather than show a broken control. */}
            {mode === 'tenant' ? (
              <Button variant="outline" onClick={testEmailConnection} disabled={testingEmail || !isConnectedEmail}>
                {testingEmail && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                Test Connection
              </Button>
            ) : <span />}
            <Button onClick={() => saveSection('email', email)} disabled={saving.email}>
              {saving.email && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Save
            </Button>
          </div>
        </CardContent>}
      </Card>

      {/* ── Card 5: SMS / Voice Provider ── */}
      <Card>
        <CardHeader className="cursor-pointer select-none" onClick={() => toggle('sms')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                <Phone className="h-4 w-4 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-base">SMS &amp; Voice Provider</CardTitle>
                <CardDescription className="text-xs mt-0">Send SMS notifications and automated voice calls to leads</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <IntegrationStatusBadge connected={isConnectedSms} />
              {expanded.sms ? <ArrowUp className="h-4 w-4 text-slate-400" /> : <ArrowDown className="h-4 w-4 text-slate-400" />}
            </div>
          </div>
        </CardHeader>
        {expanded.sms && <CardContent className="space-y-4">
          <div className="space-y-1.5">
            <Label>Provider</Label>
            <SelectField className={SELECT_CLASSNAME} value={sms.provider} onChange={e => setSms(p => ({ ...p, provider: e.target.value }))}>
              <option value="twilio">Twilio</option>
              <option value="exotel">Exotel</option>
              <option value="msg91">MSG91</option>
            </SelectField>
          </div>

          {/* Twilio */}
          {sms.provider === 'twilio' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5 col-span-2">
                <Label>Account SID</Label>
                <PwdInput id="sms_accountSid" value={sms.accountSid} onChange={v => setSms(p => ({ ...p, accountSid: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} placeholder="ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" />
              </div>
              <div className="space-y-1.5">
                <Label>Auth Token</Label>
                <PwdInput id="sms_authToken" value={sms.authToken} onChange={v => setSms(p => ({ ...p, authToken: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} />
              </div>
              <div className="space-y-1.5">
                <Label>From Number</Label>
                <Input value={sms.fromNumber} onChange={e => setSms(p => ({ ...p, fromNumber: e.target.value }))} placeholder="+1234567890" />
              </div>
            </div>
          )}

          {/* Exotel */}
          {sms.provider === 'exotel' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>API Key</Label>
                <PwdInput id="sms_apiKey" value={sms.apiKey} onChange={v => setSms(p => ({ ...p, apiKey: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} placeholder="Your API Key" />
              </div>
              <div className="space-y-1.5">
                <Label>API Secret</Label>
                <PwdInput id="sms_apiSecret" value={sms.apiSecret} onChange={v => setSms(p => ({ ...p, apiSecret: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} />
              </div>
              <div className="space-y-1.5">
                <Label>SID</Label>
                <Input value={sms.sid} onChange={e => setSms(p => ({ ...p, sid: e.target.value }))} placeholder="Your Exotel SID" />
              </div>
              <div className="space-y-1.5">
                <Label>From Number</Label>
                <Input value={sms.fromNumber} onChange={e => setSms(p => ({ ...p, fromNumber: e.target.value }))} placeholder="0XXXXXXXXXX" />
              </div>
            </div>
          )}

          {/* MSG91 */}
          {sms.provider === 'msg91' && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Auth Key</Label>
                <PwdInput id="sms_authKey" value={sms.authKey} onChange={v => setSms(p => ({ ...p, authKey: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} placeholder="xxxxxxxxxxxxxxxxxxxxxx" />
              </div>
              <div className="space-y-1.5">
                <Label>Sender ID</Label>
                <Input value={sms.senderId} onChange={e => setSms(p => ({ ...p, senderId: e.target.value }))} placeholder="SENDER" />
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <Button onClick={() => saveSection('sms', sms)} disabled={saving.sms}>
              {saving.sms && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Save
            </Button>
          </div>
        </CardContent>}
      </Card>

      {/* ── Card 6: Google Calendar (for Meet links) ── */}
      <Card>
        <CardHeader className="cursor-pointer select-none" onClick={() => toggle('gcal')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0F9D58' }}>
                <Calendar className="h-4 w-4 text-white" />
              </div>
              <div>
                <CardTitle className="text-base">Google Calendar</CardTitle>
                <CardDescription className="text-xs mt-0">Auto-generate Google Meet links and sync bookings to Google Calendar</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <IntegrationStatusBadge connected={gcalStatus.connected} />
              {expanded.gcal ? <ArrowUp className="h-4 w-4 text-slate-400" /> : <ArrowDown className="h-4 w-4 text-slate-400" />}
            </div>
          </div>
        </CardHeader>
        {expanded.gcal && <CardContent className="space-y-4">
          {gcalStatus.connected ? (
            /* ── Connected state ── */
            <div className="flex items-center justify-between gap-2 px-4 py-3 bg-emerald-50 rounded-xl border border-emerald-100">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-emerald-800">Connected{gcalStatus.email ? ` as ${gcalStatus.email}` : ''}</p>
                  <p className="text-xs text-emerald-600 mt-0.5">Bookings will sync to Google Calendar and auto-generate Meet links</p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button variant="outline" size="sm" onClick={() => handleOauthTest('google')} disabled={testingOauth === 'google'}>
                  {testingOauth === 'google' && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                  Test
                </Button>
                <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => handleOauthDisconnect('google')}>
                  Disconnect
                </Button>
              </div>
            </div>
          ) : mode === 'tenant' && gcalStatus.platformConfigured ? (
            /* ── Tenant mode: platform has credentials → one-click connect ── */
            <div className="flex flex-col items-center gap-4 py-6">
              <div className="h-16 w-16 rounded-2xl flex items-center justify-center shadow-md" style={{ backgroundColor: '#0F9D58' }}>
                <Calendar className="h-8 w-8 text-white" />
              </div>
              <div className="text-center max-w-xs">
                <p className="text-sm font-semibold text-slate-800">Connect Google Calendar</p>
                <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
                  Click below and sign in with your Google account. Bookings will automatically sync to your calendar and generate Google Meet links.
                </p>
              </div>
              <Button
                onClick={() => handleOauthConnect('google')}
                className="gap-2 px-6 h-11 text-sm font-semibold"
                style={{ backgroundColor: '#0F9D58' }}
              >
                <svg viewBox="0 0 24 24" className="h-4 w-4 fill-white"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
                Connect with Google
              </Button>
            </div>
          ) : (
            /* ── Fallback: show credentials form (tenant without platform config, or platform mode) ── */
            <>
              {(gcal.clientId && gcal.clientSecret) && (
                <div className="flex items-center justify-between gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-amber-500" />
                    <span className="text-sm text-amber-700">Credentials saved — click Connect to authorize Google</span>
                  </div>
                  <Button size="sm" onClick={() => handleOauthConnect('google')}>Connect</Button>
                </div>
              )}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <Label>Client ID</Label>
                  <Input value={gcal.clientId} onChange={e => setGcal(p => ({ ...p, clientId: e.target.value }))} placeholder="xxxx.apps.googleusercontent.com" />
                </div>
                <div className="space-y-1.5">
                  <Label>Client Secret</Label>
                  <PwdInput id="gcal_clientSecret" value={gcal.clientSecret} onChange={v => setGcal(p => ({ ...p, clientSecret: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} />
                </div>
              </div>
              <p className="text-xs text-slate-400">Get these from <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">Google Cloud Console</a>. Enable Calendar API and set the redirect URI to <code className="text-indigo-500">https://api.brixlead.com/api/availability/integrations/google/callback</code>.</p>
              <div className="flex justify-end">
                <Button onClick={() => saveSection('calendar', gcal)} disabled={saving.calendar}>
                  {saving.calendar && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                  Save
                </Button>
              </div>
            </>
          )}
        </CardContent>}
      </Card>

      {/* ── Card 7: Zoom ── */}
      <Card>
        <CardHeader className="cursor-pointer select-none" onClick={() => toggle('zoom')}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#2D8CFF' }}>
                <Video className="h-4 w-4 text-white" />
              </div>
              <div>
                <CardTitle className="text-base">Zoom</CardTitle>
                <CardDescription className="text-xs mt-0">Auto-generate Zoom meeting links for bookings</CardDescription>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <IntegrationStatusBadge connected={zoomStatus.connected} />
              {expanded.zoom ? <ArrowUp className="h-4 w-4 text-slate-400" /> : <ArrowDown className="h-4 w-4 text-slate-400" />}
            </div>
          </div>
        </CardHeader>
        {expanded.zoom && <CardContent className="space-y-4">
          {/* OAuth lifecycle banner — visible in both modes for UX parity. */}
          {(zoomStatus.connected ? (
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-emerald-50 rounded-lg border border-emerald-100">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-sm text-emerald-700">Connected{zoomStatus.email ? ` as ${zoomStatus.email}` : ''}</span>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => handleOauthTest('zoom')} disabled={testingOauth === 'zoom'}>
                  {testingOauth === 'zoom' && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                  Test
                </Button>
                <Button variant="outline" size="sm" className="text-red-600 hover:bg-red-50 hover:text-red-700" onClick={() => handleOauthDisconnect('zoom')}>
                  Disconnect
                </Button>
              </div>
            </div>
          ) : zoom.accountId ? (
            // Server-to-Server mode — no Connect/redirect; verify via Test.
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-sm text-amber-700">Server-to-Server — Save, then Test to verify</span>
              </div>
              <Button variant="outline" size="sm" onClick={() => handleOauthTest('zoom')} disabled={testingOauth === 'zoom'}>
                {testingOauth === 'zoom' && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
                Test
              </Button>
            </div>
          ) : (zoom.clientId && zoom.clientSecret) ? (
            <div className="flex items-center justify-between gap-2 px-3 py-2 bg-amber-50 rounded-lg border border-amber-100">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-amber-500" />
                <span className="text-sm text-amber-700">Credentials saved — click Connect to authorize Zoom</span>
              </div>
              <Button size="sm" onClick={() => handleOauthConnect('zoom')}>Connect</Button>
            </div>
          ) : null)}
          <div className="space-y-1.5">
            <Label>Account ID</Label>
            <Input value={zoom.accountId} onChange={e => setZoom(p => ({ ...p, accountId: e.target.value }))} placeholder="Your Zoom Account ID" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Client ID</Label>
              <Input value={zoom.clientId} onChange={e => setZoom(p => ({ ...p, clientId: e.target.value }))} placeholder="Your Zoom Client ID" />
            </div>
            <div className="space-y-1.5">
              <Label>Client Secret</Label>
              <PwdInput id="zoom_clientSecret" value={zoom.clientSecret} onChange={v => setZoom(p => ({ ...p, clientSecret: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label>Meeting Host Email</Label>
            <Input value={zoom.hostEmail} onChange={e => setZoom(p => ({ ...p, hostEmail: e.target.value }))} placeholder="e.g. admin@yourcompany.com" />
            <p className="text-xs text-slate-400">Server-to-Server: all meetings are created under this Zoom user (must exist in your Zoom account). Leave blank only for legacy user-OAuth.</p>
          </div>
          <p className="text-xs text-slate-400">
            <b>Server-to-Server OAuth</b> (recommended): fill Account ID + Client ID + Client Secret + Host Email above, then <b>Save</b> — no Connect needed.
            Get these from your app in the <a href="https://marketplace.zoom.us/develop/create" target="_blank" rel="noreferrer" className="text-indigo-500 hover:underline">Zoom App Marketplace</a>.
            (Legacy user-OAuth apps instead use the redirect URI <code className="text-indigo-500">https://api.brixlead.com/api/availability/integrations/zoom/callback</code>.)
          </p>

          {/* Meeting attendance webhooks — separate 'Secret Token' from the
              OAuth Client Secret above. Powers start/end + who-joined-first. */}
          <div className="border-t border-slate-100 pt-4 space-y-1.5">
            <Label>Webhook Secret Token</Label>
            <PwdInput id="zoom_webhookSecretToken" value={zoom.webhookSecretToken} onChange={v => setZoom(p => ({ ...p, webhookSecretToken: v }))} showMap={showPwd} onToggle={togglePwd} onReveal={revealField} />
            <p className="text-xs text-slate-400">
              In your Zoom app → <b>Event Subscriptions</b>, add this <b>notification URL</b> and copy its <b>Secret Token</b> here (this is <i>not</i> the Client Secret):
            </p>
            <div className="flex items-center gap-2">
              <code className="flex-1 truncate rounded-md bg-slate-100 px-2 py-1.5 text-xs text-slate-600">{`https://api.brixlead.com/api/webhooks/zoom/${user?.tenantId || '<your-workspace-id>'}`}</code>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText(`https://api.brixlead.com/api/webhooks/zoom/${user?.tenantId || ''}`); toast('success', 'Webhook URL copied'); }}
                className="rounded-md border border-slate-200 px-2 py-1.5 text-xs font-medium text-slate-600 hover:bg-slate-50"
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-slate-400">Subscribe to events: <b>Meeting Started/Ended</b> and <b>Participant/Host Joined/Left</b>.</p>
          </div>
          <div className="flex justify-end">
            <Button onClick={() => saveSection('zoom', zoom)} disabled={saving.zoom}>
              {saving.zoom && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
              Save
            </Button>
          </div>
        </CardContent>}
      </Card>

      {/* Disconnect confirmation dialog for Google Calendar / Zoom / Facebook */}
      <ConfirmModal
        isOpen={!!disconnectConfirm}
        title={`Disconnect ${
          disconnectConfirm === 'google' ? 'Google Calendar'
            : disconnectConfirm === 'zoom' ? 'Zoom'
            : 'Facebook'
        }?`}
        message={
          disconnectConfirm === 'google'
            ? "Future bookings won't get auto-generated Google Meet links or calendar sync until you reconnect. Your saved Client ID and Secret will be kept."
            : disconnectConfirm === 'zoom'
            ? "Future bookings won't get auto-generated Zoom links until you reconnect. Your saved Client ID and Secret will be kept."
            : "New leads from this Facebook Page will stop arriving in BrixLead until you reconnect. Your saved App ID, App Secret, and Verify Token will be kept."
        }
        confirmText="Disconnect"
        confirmButtonColor="red"
        onConfirm={confirmOauthDisconnect}
        onCancel={() => setDisconnectConfirm(null)}
        isLoading={disconnectLoading}
      />
    </div>
  );
}

// ── Financial Year Tab ───────────────────────────────────────────────────────

function FinancialYearTab() {
  const [years, setYears] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', startDate: '', endDate: '', isActive: true });
  const [saving, setSaving] = useState(false);

  const fetchYears = () => {
    setLoading(true);
    api.get('/tenant/financial-years').then(({ data }) => setYears(data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchYears(); }, []);

  const resetForm = () => { setForm({ name: '', startDate: '', endDate: '', isActive: true }); setEditId(null); setShowForm(false); };

  const handleSave = async () => {
    if (!form.name || !form.startDate || !form.endDate) { toast('error', 'All fields are required'); return; }
    setSaving(true);
    try {
      if (editId) {
        await api.patch(`/tenant/financial-years/${editId}`, form);
        toast('success', 'Financial year updated');
      } else {
        await api.post('/tenant/financial-years', form);
        toast('success', 'Financial year created');
      }
      resetForm();
      fetchYears();
    } catch { toast('error', 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleEdit = (fy: any) => {
    setEditId(fy.id);
    setForm({
      name: fy.name,
      startDate: fy.startDate.slice(0, 10),
      endDate: fy.endDate.slice(0, 10),
      isActive: fy.isActive,
    });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    try {
      await api.delete(`/tenant/financial-years/${id}`);
      toast('success', 'Financial year deleted');
      fetchYears();
    } catch { toast('error', 'Failed to delete'); }
  };

  const handleToggle = async (fy: any) => {
    try {
      await api.patch(`/tenant/financial-years/${fy.id}`, { isActive: !fy.isActive });
      toast('success', fy.isActive ? 'Deactivated' : 'Activated');
      fetchYears();
    } catch { toast('error', 'Failed to update'); }
  };

  const fmtDate = (d: string) => formatDate(d);

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Financial Year</h2>
          <p className="text-sm text-slate-500">Manage financial years for lead tracking</p>
        </div>
        <button onClick={() => { resetForm(); setShowForm(true); }}
          className="flex items-center gap-1.5 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700">
          + Add Financial Year
        </button>
      </div>

      {/* Add/Edit form */}
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4">
          <h3 className="text-sm font-bold text-slate-800">{editId ? 'Edit' : 'New'} Financial Year</h3>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-normal text-slate-500 uppercase">Financial Year Name *</label>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="e.g. FY 2025-26" className="mt-1 w-full h-10 border border-slate-200 rounded-lg px-3 text-sm" />
            </div>
            <div>
              <label className="text-xs font-normal text-slate-500 uppercase">Start Date *</label>
              <DateInput className="mt-1 w-full h-10" value={form.startDate} onChange={v => setForm(f => ({ ...f, startDate: v }))} />
            </div>
            <div>
              <label className="text-xs font-normal text-slate-500 uppercase">End Date *</label>
              <DateInput className="mt-1 w-full h-10" value={form.endDate} onChange={v => setForm(f => ({ ...f, endDate: v }))} />
            </div>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} className="accent-indigo-600" />
              <span className="text-sm text-slate-700">Set as Active</span>
            </label>
          </div>
          <div className="flex gap-2">
            <button onClick={handleSave} disabled={saving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50">
              {saving ? 'Saving...' : editId ? 'Update' : 'Create'}
            </button>
            <button onClick={resetForm} className="px-4 py-2 text-sm text-slate-500 hover:bg-slate-100 rounded-lg">Cancel</button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="text-center py-10 text-slate-400">Loading...</div>
        ) : years.length === 0 ? (
          <div className="text-center py-10 text-slate-400 text-sm">No financial years configured</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-100">
              <tr className="border-b-2 border-slate-200">
                {['Financial Year', 'Start Date', 'End Date', 'Status', 'Created By', 'Created Date', 'Actions'].map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 bg-white">
              {years.map(fy => (
                <tr key={fy.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-800">{fy.name}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(fy.startDate)}</td>
                  <td className="px-4 py-3 text-slate-600">{fmtDate(fy.endDate)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleToggle(fy)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                        fy.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'
                      }`}>
                      {fy.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{fy.createdBy?.name || '—'}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{fmtDate(fy.createdAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button onClick={() => handleEdit(fy)} className="p-1.5 rounded-md text-slate-400 hover:text-indigo-600 hover:bg-indigo-50">
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button onClick={() => handleDelete(fy.id)} className="p-1.5 rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50">
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function SettingsPage() {
  const { user, refreshBrand } = useAuth();
  // Default to Workspace — most-used tab after personal info, which
  // lives on /profile via the avatar menu.
  const [activeTab, setActiveTab] = useState<Tab>('workspace');

  const [defaultColumns, setDefaultColumns] = useState<string[]>(FACTORY_DEFAULT_COLUMNS);
  const [loadingColumns, setLoadingColumns] = useState(false);
  const [savingColumns, setSavingColumns] = useState(false);
  const [columnsSaved, setColumnsSaved] = useState(false);
  const [columnsError, setColumnsError] = useState('');

  const [brandForm, setBrandForm] = useState<BrandFormState>(createDefaultBrandForm());
  const [brandLoaded, setBrandLoaded] = useState(false);
  const [loadingBrand, setLoadingBrand] = useState(false);
  const [savingBrand, setSavingBrand] = useState(false);
  const [brandSaved, setBrandSaved] = useState(false);
  const [brandError, setBrandError] = useState('');
  const [generatingLogos, setGeneratingLogos] = useState(false);
  const [logoFileName, setLogoFileName] = useState('');
  const [countries, setCountries] = useState<string[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [cities, setCities] = useState<string[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [geoError, setGeoError] = useState('');

  const [designations, setDesignations] = useState<NamedItem[]>([]);
  const [departments, setDepartments] = useState<NamedItem[]>([]);
  const [loadingFields, setLoadingFields] = useState(false);

  const [newDesig, setNewDesig] = useState('');
  const [newDept, setNewDept] = useState('');
  const [addingDesig, setAddingDesig] = useState(false);
  const [addingDept, setAddingDept] = useState(false);

  const [editDesigId, setEditDesigId] = useState<string | null>(null);
  const [editDesigName, setEditDesigName] = useState('');
  const [editDeptId, setEditDeptId] = useState<string | null>(null);
  const [editDeptName, setEditDeptName] = useState('');
  const [confirmDelete, setConfirmDelete] = useState<{ type: 'designation' | 'department'; id: string; name: string } | null>(null);
  const [confirmDeleteLoading, setConfirmDeleteLoading] = useState(false);

  const desigInputRef = useRef<HTMLInputElement>(null);
  const deptInputRef = useRef<HTMLInputElement>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const variantUploadRef = useRef<HTMLInputElement>(null);
  const [directTarget, setDirectTarget] = useState<LogoVariantKey>('square');

  useEffect(() => {
    if (activeTab !== 'lead-columns') return;

    setLoadingColumns(true);
    api.get('/tenant/me')
      .then((response) => {
        const settings = (response.data?.settings as any) || {};
        setDefaultColumns(settings.leadDefaultColumns || FACTORY_DEFAULT_COLUMNS);
      })
      .catch(() => {})
      .finally(() => setLoadingColumns(false));
  }, [activeTab]);

  useEffect(() => {
    if (activeTab !== 'brand' || brandLoaded) return;

    setLoadingBrand(true);
    // Countries from the DB geo master.
    setLoadingCountries(true);
    setGeoError('');
    getCountryNames().then(setCountries).catch(() => {}).finally(() => setLoadingCountries(false));

    api.get('/settings')
      .then((response) => {
        setBrandForm(normalizeBrandSettings(response.data?.brandSettings));
        setBrandLoaded(true);
      })
      .catch(() => {
        setBrandError('Failed to load brand settings.');
      })
      .finally(() => setLoadingBrand(false));
  }, [activeTab, brandLoaded]);

  useEffect(() => {
    if (activeTab !== 'brand') return;
    if (!brandForm.country) {
      setStates([]);
      setCities([]);
      return;
    }

    setLoadingStates(true);
    setStates([]);
    setCities([]);
    setGeoError('');

    fetchStates(brandForm.country)
      .then(setStates)
      .catch(() => setGeoError(`Could not load states for "${brandForm.country}".`))
      .finally(() => setLoadingStates(false));
  }, [activeTab, brandForm.country]);

  useEffect(() => {
    if (activeTab !== 'brand') return;
    if (!brandForm.country || !brandForm.state) {
      setCities([]);
      return;
    }

    setLoadingCities(true);
    setCities([]);
    setGeoError('');

    fetchCities(brandForm.country, brandForm.state)
      .then(setCities)
      .catch(() => setGeoError(`Could not load cities for "${brandForm.state}".`))
      .finally(() => setLoadingCities(false));
  }, [activeTab, brandForm.country, brandForm.state]);

  useEffect(() => {
    if (activeTab !== 'user-fields') return;

    setLoadingFields(true);
    Promise.all([
      api.get('/settings/designations'),
      api.get('/settings/departments'),
    ])
      .then(([designationsResponse, departmentsResponse]) => {
        setDesignations(designationsResponse.data);
        setDepartments(departmentsResponse.data);
      })
      .catch(() => toast('error', 'Failed to load user fields'))
      .finally(() => setLoadingFields(false));
  }, [activeTab]);

  const toggleDefaultColumn = (key: string) => {
    setDefaultColumns((previous) =>
      previous.includes(key) ? previous.filter((column) => column !== key) : [...previous, key],
    );
    setColumnsSaved(false);
  };

  const selectAllDefaults = () => {
    setDefaultColumns(ALL_LEAD_COLUMNS.map((column) => column.key));
    setColumnsSaved(false);
  };

  const resetToFactory = () => {
    setDefaultColumns(FACTORY_DEFAULT_COLUMNS);
    setColumnsSaved(false);
  };

  const saveDefaultColumns = async () => {
    setSavingColumns(true);
    setColumnsError('');

    try {
      await api.patch('/settings/lead-columns', { columns: defaultColumns });
      setColumnsSaved(true);
      window.setTimeout(() => setColumnsSaved(false), 3000);
    } catch {
      setColumnsError('Failed to save. Please try again.');
    } finally {
      setSavingColumns(false);
    }
  };

  const updateBrandField = <K extends keyof BrandFormState>(key: K, value: BrandFormState[K]) => {
    setBrandForm((previous) => ({ ...previous, [key]: value }));
    setBrandSaved(false);
    setBrandError('');
  };

  const updateBrandArrayItem = (field: 'phones' | 'emails', index: number, value: string) => {
    setBrandForm((previous) => ({
      ...previous,
      [field]: previous[field].map((entry, entryIndex) => entryIndex === index ? value : entry),
    }));
    setBrandSaved(false);
  };

  const addBrandArrayItem = (field: 'phones' | 'emails') => {
    setBrandForm((previous) => ({
      ...previous,
      [field]: [...previous[field], ''],
    }));
    setBrandSaved(false);
  };

  const removeBrandArrayItem = (field: 'phones' | 'emails', index: number) => {
    setBrandForm((previous) => {
      const next = previous[field].filter((_, entryIndex) => entryIndex !== index);
      return {
        ...previous,
        [field]: next.length > 0 ? next : [''],
      };
    });
    setBrandSaved(false);
  };

  const updateLogoVariant = (variantKey: LogoVariantKey, patch: Partial<LogoVariantState>) => {
    setBrandForm((previous) => ({
      ...previous,
      logoVariants: {
        ...previous.logoVariants,
        [variantKey]: {
          ...previous.logoVariants[variantKey],
          ...patch,
        },
      },
    }));
    setBrandSaved(false);
  };

  const handleBrandCountryChange = (country: string) => {
    setBrandForm((previous) => ({
      ...previous,
      country,
      state: '',
      city: '',
    }));
    setBrandSaved(false);
  };

  const handleBrandStateChange = (state: string) => {
    setBrandForm((previous) => ({
      ...previous,
      state,
      city: '',
    }));
    setBrandSaved(false);
  };

  const handleBrandFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast('error', 'Please upload an image file.');
      return;
    }

    try {
      const src = await readFileAsDataUrl(file);
      setLogoFileName(file.name);
      setBrandForm((previous) => ({
        ...previous,
        logoSource: src,
        logoVariants: {
          square: { zoom: 1, offsetX: 0, offsetY: 0 },
          rectangular: { zoom: 1, offsetX: 0, offsetY: 0 },
        },
      }));
      setBrandSaved(false);
      setBrandError('');
    } catch {
      toast('error', 'Failed to read the image. Please try another file.');
    } finally {
      event.target.value = '';
    }
  };

  // Direct per-size upload — set that variant's image as-is (no crop-from-source).
  const pickDirectUpload = (variantKey: LogoVariantKey) => {
    setDirectTarget(variantKey);
    variantUploadRef.current?.click();
  };

  const handleVariantDirectUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast('error', 'Please upload an image file.');
      return;
    }
    try {
      const src = await readFileAsDataUrl(file);
      setBrandForm((previous) => ({
        ...previous,
        ...(directTarget === 'square' ? { logoSquare: src } : { logoRectangular: src }),
        logoVariants: {
          ...previous.logoVariants,
          [directTarget]: { zoom: 1, offsetX: 0, offsetY: 0, direct: true },
        },
      }));
      setBrandSaved(false);
      setBrandError('');
    } catch {
      toast('error', 'Failed to read the image. Please try another file.');
    } finally {
      event.target.value = '';
    }
  };

  const clearLogo = () => {
    setBrandForm((previous) => ({
      ...previous,
      logoSource: '',
      logoSquare: '',
      logoRectangular: '',
      logoVariants: createDefaultBrandForm().logoVariants,
    }));
    setLogoFileName('');
    setBrandSaved(false);
  };

  const saveBrandSettings = async () => {
    setSavingBrand(true);
    setGeneratingLogos(true);
    setBrandError('');

    try {
      let logoSquare = brandForm.logoSquare;
      let logoRectangular = brandForm.logoRectangular;

      // Directly-uploaded sizes are used as-is; only crop from the source for
      // sizes that weren't uploaded directly.
      if (brandForm.logoSource) {
        if (!brandForm.logoVariants.square.direct) {
          logoSquare = await generateCroppedLogo(
            brandForm.logoSource,
            brandForm.logoVariants.square,
            LOGO_VARIANT_META.square.width,
            LOGO_VARIANT_META.square.height,
          );
        }
        if (!brandForm.logoVariants.rectangular.direct) {
          logoRectangular = await generateCroppedLogo(
            brandForm.logoSource,
            brandForm.logoVariants.rectangular,
            LOGO_VARIANT_META.rectangular.width,
            LOGO_VARIANT_META.rectangular.height,
          );
        }
      }

      const payload = {
        organizationName: brandForm.organizationName.trim(),
        address: brandForm.address.trim(),
        country: brandForm.country.trim(),
        state: brandForm.state.trim(),
        city: brandForm.city.trim(),
        zipcode: brandForm.zipcode.trim(),
        phones: brandForm.phones.map((phone) => phone.trim()).filter(Boolean),
        emails: brandForm.emails.map((email) => email.trim()).filter(Boolean),
        websiteUrl: brandForm.websiteUrl.trim(),
        dateFormat: brandForm.dateFormat,
        currency: brandForm.currency,
        timezone: brandForm.timezone,
        language: brandForm.language,
        primaryColor: brandForm.primaryColor,
        logoSource: brandForm.logoSource,
        logoSquare,
        logoRectangular,
        logoVariants: brandForm.logoVariants,
        bookingPageSettings: brandForm.bookingPageSettings,
      };

      const response = await api.patch('/settings/brand', payload);
      setBrandForm(normalizeBrandSettings(response.data?.brandSettings));
      setBrandSaved(true);
      window.setTimeout(() => setBrandSaved(false), 3000);
      toast('success', 'Brand settings updated');
      // Refresh the cached brand in AuthContext so the sidebar logo +
      // org name update immediately without a full page reload.
      refreshBrand();
    } catch (err: any) {
      // Surface the actual API error instead of a generic message —
      // validation rejects (bad hex color, malformed URL, oversized
      // payload, etc.) all carry distinct backend messages.
      const status = err?.response?.status;
      const msg = err?.response?.data?.message;
      const errMsg = Array.isArray(msg) ? msg.join(', ') : msg;
      let display = errMsg || 'Failed to save brand settings. Please try again.';
      if (status === 413) display = 'Logo is too large. Try a smaller image (under 5 MB).';
      setBrandError(display);
      console.error('[Brand save] failed', err);
    } finally {
      setSavingBrand(false);
      setGeneratingLogos(false);
    }
  };

  const addDesignation = async () => {
    const trimmedName = newDesig.trim();
    if (!trimmedName) return;

    setAddingDesig(true);
    try {
      const response = await api.post('/settings/designations', { name: trimmedName });
      setDesignations((previous) => [...previous, response.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewDesig('');
      toast('success', 'Designation added');
    } catch (error: any) {
      toast('error', error?.response?.data?.message || 'Failed to add designation');
    } finally {
      setAddingDesig(false);
    }
  };

  const saveDesignation = async (id: string) => {
    const trimmedName = editDesigName.trim();
    if (!trimmedName) return;

    try {
      const response = await api.patch(`/settings/designations/${id}`, { name: trimmedName });
      setDesignations((previous) => previous.map((item) => item.id === id ? response.data : item).sort((a, b) => a.name.localeCompare(b.name)));
      setEditDesigId(null);
      toast('success', 'Designation updated');
    } catch (error: any) {
      toast('error', error?.response?.data?.message || 'Failed to update designation');
    }
  };

  const toggleDesignation = async (item: NamedItem) => {
    try {
      const response = await api.patch(`/settings/designations/${item.id}`, { isActive: !item.isActive });
      setDesignations((previous) => previous.map((entry) => entry.id === item.id ? response.data : entry));
    } catch {
      toast('error', 'Failed to update status');
    }
  };

  const deleteDesignation = (id: string) => {
    const item = designations.find(d => d.id === id);
    if (item) setConfirmDelete({ type: 'designation', id, name: item.name });
  };

  const addDepartment = async () => {
    const trimmedName = newDept.trim();
    if (!trimmedName) return;

    setAddingDept(true);
    try {
      const response = await api.post('/settings/departments', { name: trimmedName });
      setDepartments((previous) => [...previous, response.data].sort((a, b) => a.name.localeCompare(b.name)));
      setNewDept('');
      toast('success', 'Department added');
    } catch (error: any) {
      toast('error', error?.response?.data?.message || 'Failed to add department');
    } finally {
      setAddingDept(false);
    }
  };

  const saveDepartment = async (id: string) => {
    const trimmedName = editDeptName.trim();
    if (!trimmedName) return;

    try {
      const response = await api.patch(`/settings/departments/${id}`, { name: trimmedName });
      setDepartments((previous) => previous.map((item) => item.id === id ? response.data : item).sort((a, b) => a.name.localeCompare(b.name)));
      setEditDeptId(null);
      toast('success', 'Department updated');
    } catch (error: any) {
      toast('error', error?.response?.data?.message || 'Failed to update department');
    }
  };

  const toggleDepartment = async (item: NamedItem) => {
    try {
      const response = await api.patch(`/settings/departments/${item.id}`, { isActive: !item.isActive });
      setDepartments((previous) => previous.map((entry) => entry.id === item.id ? response.data : entry));
    } catch {
      toast('error', 'Failed to update status');
    }
  };

  const deleteDepartment = (id: string) => {
    const item = departments.find(d => d.id === id);
    if (item) setConfirmDelete({ type: 'department', id, name: item.name });
  };

  const handleConfirmFieldDelete = async () => {
    if (!confirmDelete) return;
    setConfirmDeleteLoading(true);
    try {
      if (confirmDelete.type === 'designation') {
        await api.delete(`/settings/designations/${confirmDelete.id}`);
        setDesignations(prev => prev.filter(item => item.id !== confirmDelete.id));
        toast('success', 'Designation deleted');
      } else {
        await api.delete(`/settings/departments/${confirmDelete.id}`);
        setDepartments(prev => prev.filter(item => item.id !== confirmDelete.id));
        toast('success', 'Department deleted');
      }
      setConfirmDelete(null);
    } catch {
      toast('error', `Failed to delete ${confirmDelete.type}`);
    } finally {
      setConfirmDeleteLoading(false);
    }
  };

  // Each tab gets a Lucide icon for fast scanning in the sidebar — labels
  // alone left the sidebar feeling bland (just stacked grey text). The icon
  // is also handy on mobile where the labels can wrap to two lines on small
  // screens but the icon stays a stable reference point.
  const tabs: { key: Tab; label: string; icon: typeof UserIcon }[] = [
    // Profile lives on its own /profile page (avatar dropdown). Removed
    // from here to stop the duplicate-entry confusion.
    { key: 'workspace',       label: 'Workspace',       icon: Building2 },
    { key: 'brand',           label: 'Brand Settings',  icon: Palette },
    { key: 'lead-columns',    label: 'Lead Columns',    icon: Columns3 },
    { key: 'lead-statuses',   label: 'Lead Statuses',   icon: Flag },
    { key: 'lead-sources',    label: 'Lead Sources',    icon: Inbox },
    { key: 'financial-year',  label: 'Financial Year',  icon: Calendar },
    { key: 'user-fields',     label: 'User Fields',     icon: Users },
    { key: 'custom-fields',   label: 'Custom Fields',   icon: SlidersHorizontal },
    { key: 'integrations',    label: 'Integrations',    icon: Plug },
    { key: 'communications',  label: 'Communications',  icon: Send },
  ];

  const pageWidthClassName = activeTab === 'brand' || activeTab === 'user-fields' || activeTab === 'custom-fields' || activeTab === 'integrations' || activeTab === 'lead-statuses' || activeTab === 'lead-sources' || activeTab === 'financial-year' || activeTab === 'communications' ? 'max-w-5xl' : 'max-w-2xl';

  return (
    <DashboardLayout title="Settings">
      <div className="space-y-6">
        {/* Page header — gradient brand strip with icon tile so the page
            doesn't open with bare-text "Settings" against the dashboard
            background. Matches the polish on the redesigned booking +
            developer pages. */}
        <div className="rounded-2xl bg-gradient-to-br from-indigo-50 via-white to-purple-50 border border-slate-100 p-6 sm:p-7 flex items-center gap-4">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-[#6366F1] to-[#8B5CF6] flex items-center justify-center shadow-lg shadow-indigo-200/50 flex-shrink-0">
            <SlidersHorizontal className="h-6 w-6 text-white" />
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">Settings</h1>
            <p className="text-sm text-slate-600 mt-1">Manage your account, workspace, and how leads flow through your CRM.</p>
          </div>
        </div>

        {/* Tabs layout — single bordered shell, sidebar + content joined by
            one shared frame. The active tab carries a right-pointing chevron
            that overlaps the divider and "lands" on the content area, the
            way the Credit Card mock the user referenced does it. The right
            content panel uses a Tailwind arbitrary descendant selector to
            strip the chrome (border / shadow / bg) off any nested Card so
            we don't get a second visible border just inside the outer one. */}
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col md:flex-row md:gap-0">
          {/* Tabs: horizontal scroll pill bar on mobile, vertical sidebar on md+ */}
          <div className="md:w-56 md:flex-shrink-0 md:border-r md:border-slate-200 md:bg-slate-50/40 p-3 md:p-4">
            <nav className="flex md:flex-col gap-1 md:gap-1 md:sticky md:top-4 overflow-x-auto md:overflow-visible pb-2 md:pb-0 scrollbar-thin">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                const active = activeTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`group whitespace-nowrap md:w-full md:text-left px-3 py-2.5 text-sm font-medium transition-all flex-shrink-0 flex items-center gap-2.5 ${
                      active
                        ? 'bg-gradient-to-r from-[#6366F1] to-[#8B5CF6] text-white font-semibold shadow-md shadow-indigo-200/60 rounded-xl md:rounded-r-none md:-mr-px md:relative md:z-10 md:after:content-[""] md:after:absolute md:after:left-full md:after:top-1/2 md:after:-translate-y-1/2 md:after:w-0 md:after:h-0 md:after:border-y-[7px] md:after:border-y-transparent md:after:border-l-[7px] md:after:border-l-[#8B5CF6]'
                        : 'rounded-xl text-slate-600 hover:text-slate-900 hover:bg-slate-100/70'
                    }`}
                  >
                    <Icon
                      className={`h-4 w-4 flex-shrink-0 ${
                        active ? 'text-white' : 'text-slate-400 group-hover:text-slate-600'
                      }`}
                    />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Right content — strip nested Card chrome (.bg-card is the marker
              shadcn Card uses) so the only visible border on this side is
              the outer wrapper's, no more parallel-line "double border". */}
          <div className="flex-1 min-w-0 p-4 sm:p-6 [&_.bg-card]:!border-0 [&_.bg-card]:!shadow-none [&_.bg-card]:!bg-transparent [&_.bg-card]:!rounded-none">

        {/* Profile panel removed — see /profile page (avatar dropdown).
            Keeping the rest of the activeTab blocks below as-is. */}

        {activeTab === 'workspace' && (
          <WorkspaceTab tenantId={user?.tenantId || ''} />
        )}

        {activeTab === 'brand' && (
          <Card>
            <CardHeader>
              <CardTitle>Brand Settings</CardTitle>
              <CardDescription>Control logos, contact details, locale preferences, and vendor branding.</CardDescription>
            </CardHeader>
            <CardContent>
              {loadingBrand ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="space-y-8">
                  <input
                    id="brand-logo-upload"
                    ref={logoInputRef}
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    tabIndex={-1}
                    aria-hidden="true"
                    onChange={handleBrandFileUpload}
                  />
                  <input
                    ref={variantUploadRef}
                    type="file"
                    accept="image/*"
                    className="sr-only"
                    tabIndex={-1}
                    aria-hidden="true"
                    onChange={handleVariantDirectUpload}
                  />

                  <section className="space-y-4 rounded-xl border border-slate-200 p-5">
                    <div className="flex flex-col gap-1">
                      <h3 className="text-base font-semibold text-slate-900">Logo Upload and Crop</h3>
                      <p className="text-sm text-slate-500">Upload one logo source and tune separate square and rectangular crops for dashboard and email use.</p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      <Button
                        type="button"
                        onClick={() => logoInputRef.current?.click()}
                        className="bg-[#6366F1] text-white hover:bg-[#5254cc]"
                      >
                        <Upload className="mr-2 h-4 w-4" />
                        Upload Logo
                      </Button>
                      {brandForm.logoSource && (
                        <Button type="button" variant="outline" onClick={clearLogo}>
                          Remove Logo
                        </Button>
                      )}
                      {logoFileName && (
                        <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600">
                          {logoFileName}
                        </span>
                      )}
                    </div>

                    <div className="grid gap-5 lg:grid-cols-2">
                        {(['square', 'rectangular'] as LogoVariantKey[]).map((variantKey) => {
                          const meta = LOGO_VARIANT_META[variantKey];
                          const variant = brandForm.logoVariants[variantKey];
                          const preview = variantKey === 'square' ? brandForm.logoSquare : brandForm.logoRectangular;
                          const isDirect = !!variant.direct;
                          const previewBackground = preview || brandForm.logoSource;

                          return (
                            <div key={variantKey} className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <h4 className="text-sm font-semibold text-slate-900">{meta.label}</h4>
                                  <p className="text-xs text-slate-500">{meta.width} × {meta.height} export</p>
                                </div>
                                <Button type="button" size="sm" variant="outline" onClick={() => pickDirectUpload(variantKey)}>
                                  <Upload className="mr-1.5 h-3.5 w-3.5" /> Upload this size
                                </Button>
                              </div>

                              <div className={`overflow-hidden rounded-xl border border-slate-200 bg-white ${meta.previewClassName}`}>
                                {isDirect && preview ? (
                                  <img src={preview} alt={`${meta.label} preview`} className="h-full w-full object-contain p-2" />
                                ) : previewBackground ? (
                                  <div className="relative h-full w-full overflow-hidden bg-[linear-gradient(135deg,#f8fafc,#eef2ff)]">
                                    <img
                                      src={brandForm.logoSource || preview}
                                      alt={`${meta.label} preview`}
                                      className="absolute left-1/2 top-1/2 max-w-none"
                                      style={{
                                        transform: `translate(calc(-50% + ${variant.offsetX}%), calc(-50% + ${variant.offsetY}%)) scale(${variant.zoom})`,
                                        transformOrigin: 'center',
                                      }}
                                    />
                                  </div>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => pickDirectUpload(variantKey)}
                                    className="flex h-full w-full flex-col items-center justify-center gap-1 text-slate-400 hover:text-[#6366F1]"
                                  >
                                    <ImagePlus className="h-8 w-8" />
                                    <span className="text-xs font-medium">Upload {meta.width}×{meta.height}</span>
                                  </button>
                                )}
                              </div>

                              {isDirect ? (
                                <p className="rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-700">
                                  Uploaded directly at {meta.width}×{meta.height}. Used as-is — no cropping applied. Use “Upload this size” again to replace it.
                                </p>
                              ) : brandForm.logoSource ? (
                                <div className="space-y-3">
                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs font-medium text-slate-500">
                                      <span>Zoom</span>
                                      <span>{variant.zoom.toFixed(1)}x</span>
                                    </div>
                                    <input
                                      type="range"
                                      min="1"
                                      max="3"
                                      step="0.1"
                                      value={variant.zoom}
                                      onChange={(event) => updateLogoVariant(variantKey, { zoom: Number(event.target.value) })}
                                      className="w-full accent-[#6366F1]"
                                    />
                                  </div>

                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs font-medium text-slate-500">
                                      <span>Horizontal Crop</span>
                                      <span>{variant.offsetX}%</span>
                                    </div>
                                    <input
                                      type="range"
                                      min="-100"
                                      max="100"
                                      step="1"
                                      value={variant.offsetX}
                                      onChange={(event) => updateLogoVariant(variantKey, { offsetX: clamp(Number(event.target.value), -100, 100) })}
                                      className="w-full accent-[#6366F1]"
                                    />
                                  </div>

                                  <div className="space-y-1">
                                    <div className="flex items-center justify-between text-xs font-medium text-slate-500">
                                      <span>Vertical Crop</span>
                                      <span>{variant.offsetY}%</span>
                                    </div>
                                    <input
                                      type="range"
                                      min="-100"
                                      max="100"
                                      step="1"
                                      value={variant.offsetY}
                                      onChange={(event) => updateLogoVariant(variantKey, { offsetY: clamp(Number(event.target.value), -100, 100) })}
                                      className="w-full accent-[#6366F1]"
                                    />
                                  </div>
                                </div>
                              ) : (
                                <p className="text-xs text-slate-500">
                                  Upload a source image above to crop, or use “Upload this size” to add a ready-made {meta.width}×{meta.height} image directly.
                                </p>
                              )}
                            </div>
                          );
                        })}
                      </div>
                  </section>

                  <section className="space-y-6">
                    <div className="space-y-5 rounded-xl border border-slate-200 p-5">
                      <div>
                        <h3 className="text-base font-semibold text-slate-900">Organization</h3>
                        <p className="text-sm text-slate-500">Display name and location used across dashboards, communication, and lead routing.</p>
                      </div>

                      <div className="space-y-2">
                        <Label>Organization Name</Label>
                        <Input
                          placeholder="Acme Realty"
                          value={brandForm.organizationName}
                          onChange={(event) => updateBrandField('organizationName', event.target.value)}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Address</Label>
                        <textarea
                          className={TEXTAREA_CLASSNAME}
                          placeholder="Full postal address"
                          value={brandForm.address}
                          onChange={(event) => updateBrandField('address', event.target.value)}
                        />
                      </div>

                      <div className="grid gap-4 sm:grid-cols-3">
                        <div className="space-y-2">
                          <Label>Country</Label>
                          <SearchableSelect
                            options={countries}
                            value={brandForm.country}
                            onChange={handleBrandCountryChange}
                            placeholder="Select country"
                            loading={loadingCountries}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>State</Label>
                          <SearchableSelect
                            options={states}
                            value={brandForm.state}
                            onChange={handleBrandStateChange}
                            placeholder={!brandForm.country ? 'Select country first' : loadingStates ? 'Loading…' : states.length === 0 ? 'N/A' : 'Select state'}
                            disabled={!brandForm.country || loadingStates}
                            loading={loadingStates}
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>City</Label>
                          <SearchableSelect
                            options={cities.length > 0 ? cities : (brandForm.state ? [brandForm.state] : [])}
                            value={brandForm.city}
                            onChange={(value) => updateBrandField('city', value)}
                            placeholder={!brandForm.state ? 'Select state first' : loadingCities ? 'Loading…' : cities.length > 0 ? 'Select city' : 'Use state as city'}
                            disabled={!brandForm.state || loadingCities}
                            loading={loadingCities}
                          />
                        </div>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-3">
                        <div className="space-y-2">
                          <Label>Pincode</Label>
                          <Input
                            placeholder="Zip / postal code"
                            value={brandForm.zipcode}
                            onChange={(event) => updateBrandField('zipcode', event.target.value)}
                          />
                        </div>
                      </div>

                      {geoError && (
                        <div className="flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
                          <AlertCircle className="h-4 w-4 shrink-0" />
                          <span>{geoError}</span>
                        </div>
                      )}
                    </div>
                  </section>

                  <section className="space-y-6">
                    <div className="space-y-5 rounded-xl border border-slate-200 p-5">
                      <div>
                        <h3 className="text-base font-semibold text-slate-900">Contact Details</h3>
                        <p className="text-sm text-slate-500">Add multiple phone numbers, email addresses, and a website URL.</p>
                      </div>

                      <div className="grid grid-cols-2 gap-6">
                        <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-800 whitespace-nowrap">Phone Numbers</p>
                              <p className="text-xs text-slate-500 mt-0.5">Primary and additional contact numbers</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => addBrandArrayItem('phones')}
                              className="flex-shrink-0 whitespace-nowrap text-sm font-medium text-[#6366F1] hover:underline"
                            >
                              + Add Phone
                            </button>
                          </div>
                          {brandForm.phones.map((phone, index) => (
                            <div key={`phone-${index}`} className="flex items-center gap-2 rounded-lg bg-white p-2">
                              <PhoneInput
                                value={parsePhoneString(phone)}
                                onChange={(value) => updateBrandArrayItem('phones', index, formatPhoneString(value))}
                                placeholder={index === 0 ? '9876543210' : 'Additional phone number'}
                              />
                              <button
                                type="button"
                                onClick={() => removeBrandArrayItem('phones', index)}
                                className="rounded-md p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                                aria-label={`Remove phone ${index + 1}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>

                        <div className="space-y-4 rounded-xl border border-slate-200 bg-slate-50/60 p-4">
                          <div className="flex items-center justify-between gap-3 mb-2">
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-800 whitespace-nowrap">Email Addresses</p>
                              <p className="text-xs text-slate-500 mt-0.5">Inbox addresses used for outreach and responses</p>
                            </div>
                            <button
                              type="button"
                              onClick={() => addBrandArrayItem('emails')}
                              className="flex-shrink-0 whitespace-nowrap text-sm font-medium text-[#6366F1] hover:underline"
                            >
                              + Add Email
                            </button>
                          </div>
                          {brandForm.emails.map((email, index) => (
                            <div key={`email-${index}`} className="flex items-center gap-2 rounded-lg bg-white p-2">
                              <Input
                                placeholder={index === 0 ? 'hello@company.com' : 'Additional email'}
                                value={email}
                                onChange={(event) => updateBrandArrayItem('emails', index, event.target.value)}
                              />
                              <button
                                type="button"
                                onClick={() => removeBrandArrayItem('emails', index)}
                                className="rounded-md p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-500"
                                aria-label={`Remove email ${index + 1}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>Website URL</Label>
                        <Input
                          placeholder="https://example.com"
                          value={brandForm.websiteUrl}
                          onChange={(event) => updateBrandField('websiteUrl', event.target.value)}
                        />
                      </div>
                    </div>

                    <div className="space-y-5 rounded-xl border border-slate-200 p-5">
                      <div>
                        <h3 className="text-base font-semibold text-slate-900">Regional Preferences</h3>
                        <p className="text-sm text-slate-500">Set display formats, currency, timezone, language, and theme color.</p>
                      </div>

                      <div className="grid gap-4 sm:grid-cols-2">
                        <div className="space-y-2">
                          <Label>Date Format</Label>
                          <SelectField
                            className={SELECT_CLASSNAME}
                            value={brandForm.dateFormat}
                            onChange={(event) => updateBrandField('dateFormat', event.target.value)}
                          >
                            {DATE_FORMAT_OPTIONS.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </SelectField>
                        </div>

                        <div className="space-y-2">
                          <Label>Currency</Label>
                          <SelectField
                            className={SELECT_CLASSNAME}
                            value={brandForm.currency}
                            onChange={(event) => updateBrandField('currency', event.target.value)}
                          >
                            {CURRENCY_OPTIONS.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </SelectField>
                        </div>

                        <div className="space-y-2">
                          <Label>Timezone</Label>
                          <SelectField
                            className={SELECT_CLASSNAME}
                            value={brandForm.timezone}
                            onChange={(event) => updateBrandField('timezone', event.target.value)}
                          >
                            {TIMEZONE_OPTIONS.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </SelectField>
                        </div>

                        <div className="space-y-2">
                          <Label>Language</Label>
                          <SelectField
                            className={SELECT_CLASSNAME}
                            value={brandForm.language}
                            onChange={(event) => updateBrandField('language', event.target.value)}
                          >
                            {LANGUAGE_OPTIONS.map((option) => (
                              <option key={option} value={option}>{option}</option>
                            ))}
                          </SelectField>
                        </div>

                        <div className="space-y-2">
                          <Label>Primary Color</Label>
                          <div className="flex items-center gap-3">
                            <input
                              type="color"
                              value={brandForm.primaryColor}
                              onChange={(event) => updateBrandField('primaryColor', event.target.value)}
                              className="h-10 w-14 cursor-pointer rounded-md border border-slate-200 bg-white p-1"
                            />
                            <Input
                              value={brandForm.primaryColor}
                              onChange={(event) => updateBrandField('primaryColor', event.target.value)}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>

                  {/* Booking Page Settings */}
                  <section className="space-y-4 rounded-xl border border-slate-200 p-5">
                    <div className="flex flex-col gap-1">
                      <h3 className="text-base font-semibold text-slate-900">Booking Page Settings</h3>
                      <p className="text-sm text-slate-500">Customize messages and contact visibility on your public booking page.</p>
                    </div>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                      <div className="space-y-1.5">
                        <Label>Welcome Message</Label>
                        <Input
                          value={brandForm.bookingPageSettings.welcomeMessage}
                          onChange={(e) => updateBrandField('bookingPageSettings', { ...brandForm.bookingPageSettings, welcomeMessage: e.target.value })}
                          placeholder="Book a time that works for you"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label>Thank You Message</Label>
                        <Input
                          value={brandForm.bookingPageSettings.thankYouMessage}
                          onChange={(e) => updateBrandField('bookingPageSettings', { ...brandForm.bookingPageSettings, thankYouMessage: e.target.value })}
                          placeholder="Thanks for booking! See you soon."
                        />
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label>Footer Note</Label>
                        <Input
                          value={brandForm.bookingPageSettings.footerNote}
                          onChange={(e) => updateBrandField('bookingPageSettings', { ...brandForm.bookingPageSettings, footerNote: e.target.value })}
                          placeholder="We'll send you a calendar invite and meeting link"
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-6 pt-1">
                      {(['showPhone', 'showEmail', 'showWebsite'] as const).map((key) => {
                        const labels = { showPhone: 'Show Phone', showEmail: 'Show Email', showWebsite: 'Show Website' };
                        return (
                          <label key={key} className="flex items-center gap-2 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={brandForm.bookingPageSettings[key]}
                              onChange={(e) => updateBrandField('bookingPageSettings', { ...brandForm.bookingPageSettings, [key]: e.target.checked })}
                              className="h-4 w-4 rounded border-slate-300 accent-[#6366F1]"
                            />
                            <span className="text-sm text-slate-700">{labels[key]}</span>
                          </label>
                        );
                      })}
                    </div>
                  </section>

                  <div className="flex flex-wrap items-center gap-3 border-t border-slate-100 pt-2">
                    <Button
                      type="button"
                      onClick={saveBrandSettings}
                      disabled={savingBrand}
                      className="bg-[#6366F1] text-white hover:bg-[#5254cc]"
                    >
                      {(savingBrand || generatingLogos) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save Brand Settings
                    </Button>
                    {brandSaved && (
                      <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-600">
                        <Check className="h-4 w-4" />
                        Saved
                      </span>
                    )}
                    {brandError && <span className="text-sm text-red-500">{brandError}</span>}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'lead-columns' && (
          <Card>
            <CardHeader>
              <CardTitle>Lead Columns</CardTitle>
              <CardDescription>
                Choose which columns are visible by default in the Leads list for all users.
                Individual users can still customize their own view.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingColumns ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <button
                      onClick={selectAllDefaults}
                      className="text-xs font-semibold text-[#6366F1] hover:underline"
                    >
                      Select All
                    </button>
                    <span className="text-xs text-slate-300">|</span>
                    <button
                      onClick={resetToFactory}
                      className="text-xs font-semibold text-slate-500 hover:underline"
                    >
                      Reset to Default
                    </button>
                    <span className="ml-auto text-xs text-slate-400">
                      {defaultColumns.length} of {ALL_LEAD_COLUMNS.length} selected
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-1 rounded-lg border border-slate-100 bg-slate-50/50 p-3">
                    {ALL_LEAD_COLUMNS.map((column) => (
                      <label
                        key={column.key}
                        className="group flex cursor-pointer items-center gap-2.5 rounded-md border border-transparent px-3 py-2 transition-all hover:border-slate-200 hover:bg-white"
                      >
                        <input
                          type="checkbox"
                          checked={defaultColumns.includes(column.key)}
                          onChange={() => toggleDefaultColumn(column.key)}
                          className="h-4 w-4 cursor-pointer rounded border-slate-300 text-[#6366F1] accent-[#6366F1]"
                        />
                        <span className="select-none text-sm text-slate-700 group-hover:text-slate-900">
                          {column.label}
                        </span>
                      </label>
                    ))}
                  </div>

                  <div className="flex items-center gap-3 pt-1">
                    <Button
                      onClick={saveDefaultColumns}
                      disabled={savingColumns}
                      className="bg-[#6366F1] text-white hover:bg-[#5254cc]"
                    >
                      {savingColumns && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                      Save Default Columns
                    </Button>
                    {columnsSaved && (
                      <span className="flex items-center gap-1.5 text-sm font-medium text-emerald-600">
                        <Check className="h-4 w-4" />
                        Saved
                      </span>
                    )}
                    {columnsError && <span className="text-sm text-red-500">{columnsError}</span>}
                  </div>

                  <p className="border-t border-slate-100 pt-3 text-xs text-slate-400">
                    Users who have not customized their columns will see these defaults.
                    Existing user preferences are preserved.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {activeTab === 'lead-statuses' && (
          <LeadStatusesTab />
        )}

        {activeTab === 'lead-sources' && (
          <LeadSourcesTab />
        )}

        {activeTab === 'financial-year' && (
          <FinancialYearTab />
        )}
        {activeTab === 'custom-fields' && (
          <CustomFieldsTab />
        )}
        {activeTab === 'integrations' && (
          <IntegrationsTab />
        )}
        {activeTab === 'communications' && (
          <CommunicationsSettings />
        )}
        {activeTab === 'user-fields' && (
          <div className="grid grid-cols-2 gap-6">
            {loadingFields ? (
              <div className="col-span-2 flex items-center justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : (
              <>
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Designation Master</CardTitle>
                    <CardDescription>Job titles used when creating users</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        ref={desigInputRef}
                        placeholder="Add designation..."
                        value={newDesig}
                        onChange={(event) => setNewDesig(event.target.value)}
                        onKeyDown={(event) => event.key === 'Enter' && addDesignation()}
                        className="h-8 text-sm"
                      />
                      <Button
                        size="sm"
                        onClick={addDesignation}
                        disabled={addingDesig || !newDesig.trim()}
                        className="h-8 shrink-0 bg-[#6366F1] px-3 text-white hover:bg-[#5254cc]"
                      >
                        {addingDesig ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                      </Button>
                    </div>

                    <div className="overflow-hidden rounded-md border border-slate-100">
                      <div className="divide-y divide-slate-100">
                        {designations.length === 0 ? (
                          <p className="py-6 text-center text-sm text-slate-400">No designations yet</p>
                        ) : (
                          designations.map((item) => (
                            <div key={item.id} className="group flex items-center gap-2 bg-white px-3 py-2 hover:bg-slate-50">
                              {editDesigId === item.id ? (
                                <>
                                  <Input
                                    value={editDesigName}
                                    onChange={(event) => setEditDesigName(event.target.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') saveDesignation(item.id);
                                      if (event.key === 'Escape') setEditDesigId(null);
                                    }}
                                    className="h-7 flex-1 text-sm"
                                    autoFocus
                                  />
                                  <button onClick={() => saveDesignation(item.id)} className="text-emerald-600 hover:text-emerald-700">
                                    <Check className="h-4 w-4" />
                                  </button>
                                  <button onClick={() => setEditDesigId(null)} className="text-slate-400 hover:text-slate-600">
                                    <X className="h-4 w-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <span className={`flex-1 text-sm ${item.isActive ? 'text-slate-800' : 'text-slate-400 line-through'}`}>
                                    {item.name}
                                  </span>
                                  <button
                                    onClick={() => toggleDesignation(item)}
                                    className={`opacity-0 transition-opacity group-hover:opacity-100 ${item.isActive ? 'text-emerald-500 hover:text-emerald-600' : 'text-slate-300 hover:text-slate-400'}`}
                                    title={item.isActive ? 'Deactivate' : 'Activate'}
                                  >
                                    {item.isActive ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                                  </button>
                                  <button
                                    onClick={() => { setEditDesigId(item.id); setEditDesigName(item.name); }}
                                    className="text-slate-400 opacity-0 transition-opacity hover:text-[#6366F1] group-hover:opacity-100"
                                    title="Edit"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={() => deleteDesignation(item.id)}
                                    className="text-slate-400 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-slate-400">{designations.length} designation{designations.length !== 1 ? 's' : ''}</p>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base">Department Master</CardTitle>
                    <CardDescription>Departments used when creating users</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex gap-2">
                      <Input
                        ref={deptInputRef}
                        placeholder="Add department..."
                        value={newDept}
                        onChange={(event) => setNewDept(event.target.value)}
                        onKeyDown={(event) => event.key === 'Enter' && addDepartment()}
                        className="h-8 text-sm"
                      />
                      <Button
                        size="sm"
                        onClick={addDepartment}
                        disabled={addingDept || !newDept.trim()}
                        className="h-8 shrink-0 bg-[#6366F1] px-3 text-white hover:bg-[#5254cc]"
                      >
                        {addingDept ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                      </Button>
                    </div>

                    <div className="overflow-hidden rounded-md border border-slate-100">
                      <div className="divide-y divide-slate-100">
                        {departments.length === 0 ? (
                          <p className="py-6 text-center text-sm text-slate-400">No departments yet</p>
                        ) : (
                          departments.map((item) => (
                            <div key={item.id} className="group flex items-center gap-2 bg-white px-3 py-2 hover:bg-slate-50">
                              {editDeptId === item.id ? (
                                <>
                                  <Input
                                    value={editDeptName}
                                    onChange={(event) => setEditDeptName(event.target.value)}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter') saveDepartment(item.id);
                                      if (event.key === 'Escape') setEditDeptId(null);
                                    }}
                                    className="h-7 flex-1 text-sm"
                                    autoFocus
                                  />
                                  <button onClick={() => saveDepartment(item.id)} className="text-emerald-600 hover:text-emerald-700">
                                    <Check className="h-4 w-4" />
                                  </button>
                                  <button onClick={() => setEditDeptId(null)} className="text-slate-400 hover:text-slate-600">
                                    <X className="h-4 w-4" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <span className={`flex-1 text-sm ${item.isActive ? 'text-slate-800' : 'text-slate-400 line-through'}`}>
                                    {item.name}
                                  </span>
                                  <button
                                    onClick={() => toggleDepartment(item)}
                                    className={`opacity-0 transition-opacity group-hover:opacity-100 ${item.isActive ? 'text-emerald-500 hover:text-emerald-600' : 'text-slate-300 hover:text-slate-400'}`}
                                    title={item.isActive ? 'Deactivate' : 'Activate'}
                                  >
                                    {item.isActive ? <ToggleRight className="h-4 w-4" /> : <ToggleLeft className="h-4 w-4" />}
                                  </button>
                                  <button
                                    onClick={() => { setEditDeptId(item.id); setEditDeptName(item.name); }}
                                    className="text-slate-400 opacity-0 transition-opacity hover:text-[#6366F1] group-hover:opacity-100"
                                    title="Edit"
                                  >
                                    <Pencil className="h-3.5 w-3.5" />
                                  </button>
                                  <button
                                    onClick={() => deleteDepartment(item.id)}
                                    className="text-slate-400 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </>
                              )}
                            </div>
                          ))
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-slate-400">{departments.length} department{departments.length !== 1 ? 's' : ''}</p>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        )}
          </div>{/* close flex-1 content */}
        </div>{/* close flex gap-6 */}
      </div>
      <ConfirmModal
        isOpen={!!confirmDelete}
        title={`Delete ${confirmDelete?.type === 'designation' ? 'Designation' : 'Department'}?`}
        message={`Are you sure you want to delete "${confirmDelete?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        onConfirm={handleConfirmFieldDelete}
        onCancel={() => setConfirmDelete(null)}
        isLoading={confirmDeleteLoading}
      />
    </DashboardLayout>
  );
}
